import ContentWorkReport, {ContentWorkReportDetail} from '../components/ContentWorkReport';
import ReportReviewPanel from '../components/ReportReviewPanel';
import ReportingLineReview from '../components/ReportingLineReview';
import React, { useState, useEffect } from 'react';
import { get, post, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const REPORT_TYPES = {
  factory_supervisor: { label: 'Production Team Lead Report', roles: ['production_team_lead','production_supervisor'] },
  brand_content_work: { label: 'Brand & Content Work Report', roles: ['brand_content_officer'] },
  social_media: { label: 'Social Media / Content Report', roles: ['content_sales'] },
  customer_experience: { label: 'Customer Experience Report', roles: ['sales_manager','content_sales'] },
  internal_admin: { label: 'Internal Admin Report', roles: ['admin'] },
  inventory_officer: { label: 'Inventory Officer Report', roles: ['inventory_officer'] },
};

const FREQUENCIES = ['Daily', 'Weekly', 'Monthly'];

export default function TeamReports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showSubmit, setShowSubmit] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterFreq, setFilterFreq] = useState('');
  const [showReportingReview,setShowReportingReview] = useState(false);

  const isAdmin = ['super_admin','admin'].includes(user?.role);
  const canSubmit = Object.entries(REPORT_TYPES).some(([,rt]) => rt.roles.includes(user?.role)) || isAdmin;

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (filterType) qs.append('report_type', filterType);
    if (filterFreq) qs.append('frequency', filterFreq.toLowerCase());
    get('/team-reports?'+qs.toString()).then(d => setReports(d.data||[])).catch(err=>{setReports([]);window.alert(err.message);}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filterType, filterFreq]);

  const handleView = (report) => {
    setSelectedReport(null);
    get('/team-reports/'+report.id).then(data=>{setSelectedReport(data);setView('detail');}).catch(err=>window.alert(err.message));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this report?')) return;
    try { await del('/team-reports/'+id); load(); }
    catch (err) { alert(err.message); }
  };

  const getReportTypeBadge = (type) => {
    const colors = {
      factory_supervisor: { bg:'#FFF3DC', color:'#8B5E0A' },
      social_media: { bg:'#EDF4FF', color:'#1E4FAD' },
      customer_experience: { bg:'#E8F5EB', color:'#2E6B3A' },
      internal_admin: { bg:'#F2EFE7', color:'#556B2F' },
      inventory_officer: { bg:'#EDF4FF', color:'#1E4FAD' },
    };
    const c = colors[type] || { bg:'#F0F0F0', color:'#8A8A8A' };
    const label = REPORT_TYPES[type]?.label || type;
    return <span style={{padding:'3px 10px',borderRadius:20,fontSize:10,fontWeight:600,background:c.bg,color:c.color}}>{label}</span>;
  };

  const getFreqBadge = (freq) => {
    const colors = { daily:'#FDEAEA', weekly:'#EDF4FF', monthly:'#E8F5EB' };
    const textColors = { daily:'#8F2E24', weekly:'#1E4FAD', monthly:'#2E6B3A' };
    const f = freq?.toLowerCase();
    return <span style={{padding:'3px 8px',borderRadius:20,fontSize:9,fontWeight:700,textTransform:'uppercase',background:colors[f]||'#F0F0F0',color:textColors[f]||'#8A8A8A'}}>{freq}</span>;
  };

  if(showReportingReview) return <ReportingLineReview onBack={()=>{setShowReportingReview(false);load();}} renderStructured={report=><ReportDetailView embedded report={report} onBack={()=>{}} onDelete={handleDelete} isAdmin={isAdmin} userRole={user?.role} />} />;

  if (view === 'detail' && selectedReport) {
    return <ReportDetailView report={selectedReport} onBack={() => { setView('list'); setSelectedReport(null); }} onDelete={handleDelete} isAdmin={isAdmin} userRole={user?.role} />;
  }

  if (showSubmit) {
    return <SubmitReportView user={user} onBack={() => { setShowSubmit(false); load(); }} />;
  }

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Team Reports</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Daily, weekly and monthly staff reports</p>
        </div>
        {canSubmit && (
          <button className="btn btn-primary" onClick={() => setShowSubmit(true)}>+ Submit Report</button>
        )}
      </div>

      <div style={{marginBottom:16}}><button className="btn btn-primary" onClick={()=>setShowReportingReview(true)}>Review Staff Reports &amp; Checklists</button></div>

      {/* Filters */}
      <div className="search-bar" style={{marginBottom:16,flexWrap:'wrap'}}>
        <select style={{fontSize:12,padding:'6px 10px',border:'1px solid var(--border-light)',borderRadius:6}}
          value={filterType} onChange={e=>setFilterType(e.target.value)}>
          <option value="">All Report Types</option>
          {Object.entries(REPORT_TYPES).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select style={{fontSize:12,padding:'6px 10px',border:'1px solid var(--border-light)',borderRadius:6}}
          value={filterFreq} onChange={e=>setFilterFreq(e.target.value)}>
          <option value="">All Frequencies</option>
          {FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        {(filterType||filterFreq) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setFilterType(''); setFilterFreq(''); }}>Clear</button>
        )}
      </div>

      {loading ? <div className="spinner"></div> : reports.length === 0 ? (
        <div className="empty-state">
          <h3>No reports yet</h3>
          <p>Staff reports will appear here once submitted.</p>
        </div>
      ) : (
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Report Type</th>
                <th>Frequency</th>
                <th>Period</th>
                <th>Submitted By</th>
                <th>Date Submitted</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {reports.map(r => (
                <tr key={r.id} style={{background:(!r.is_read_by_director&&user?.role==='super_admin')||(!r.is_read_by_admin&&user?.role==='admin')?'#FFFBF0':''}}>
                  <td>{getReportTypeBadge(r.report_type)}</td>
                  <td>{getFreqBadge(r.frequency)}</td>
                  <td style={{fontWeight:500,fontSize:13}}>{r.period_label}</td>
                  <td style={{fontSize:13}}>{r.submitted_by_name}</td>
                  <td style={{fontSize:12,color:'#8A8A8A'}}>{new Date(r.created_at).toLocaleDateString('en-NG')}</td>
                  <td>
                    {((!r.is_read_by_director&&user?.role==='super_admin')||(!r.is_read_by_admin&&user?.role==='admin')) && (
                      <span style={{fontSize:9,fontWeight:700,color:'#fff',background:'#8F2E24',padding:'2px 8px',borderRadius:20}}>NEW</span>
                    )}
                  </td>
                  <td>
                    <div className="flex-center gap-sm">
                      <button className="btn btn-ghost btn-sm" onClick={() => handleView(r)}>View</button>
                      {user?.role==='super_admin' && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(r.id)}>Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReportDetailView({ report, onBack, onDelete, isAdmin, userRole, embedded=false }) {
  const sections = report.sections || {};
  const reportType = report.report_type;
  const frequency = report.frequency?.toLowerCase();

  const printReport = () => {
    window.print();
  };

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          {!embedded && <button className="btn btn-ghost btn-sm" onClick={onBack} style={{marginBottom:8}}>← Back to Reports</button>}
          <h2 className="page-title" style={{fontSize:'1.2rem'}}>{report.period_label}</h2>
          <p className="text-muted text-sm">Submitted by {report.submitted_by_name} on {new Date(report.created_at).toLocaleDateString('en-NG')}</p>
        </div>
        <button className="btn btn-outline" onClick={printReport}>Print Report</button>
      </div>

      {!embedded && <ReportReviewPanel kind="structured" recordId={report.id} />}

      <div style={{background:'#556B2F',borderRadius:10,padding:'14px 20px',marginBottom:20,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div>
          <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.2rem',color:'#A8893D',fontWeight:600}}>
            {report.report_type==='brand_content_work'?'Brand & Content Work Report':
             report.report_type==='factory_supervisor'?'Factory Supervisor Report':
             report.report_type==='social_media'?'Social Media / Content Report':
             report.report_type==='customer_experience'?'Customer Experience Report':
             'Internal Admin Report'}
          </div>
          <div style={{fontSize:11,color:'rgba(242,239,231,.5)',marginTop:2,textTransform:'capitalize'}}>{report.frequency} Report</div>
        </div>
        <div style={{textAlign:'right',fontSize:12,color:'rgba(242,239,231,.6)'}}>
          <div>{report.period_label}</div>
          <div style={{marginTop:2}}>{report.submitted_by_name}</div>
        </div>
      </div>

      {reportType==='brand_content_work' && <ContentWorkReportDetail sections={sections} frequency={frequency} />}
      {reportType==='factory_supervisor' && frequency==='daily' && <FactoryDailyDetail sections={sections} />}
      {reportType==='factory_supervisor' && frequency==='weekly' && <FactoryWeeklyDetail sections={sections} />}
      {reportType==='factory_supervisor' && frequency==='monthly' && <FactoryMonthlyDetail sections={sections} />}
      {reportType==='social_media' && frequency==='daily' && <SocialDailyDetail sections={sections} />}
      {reportType==='social_media' && frequency==='weekly' && <SocialWeeklyDetail sections={sections} />}
      {reportType==='social_media' && frequency==='monthly' && <SocialMonthlyDetail sections={sections} />}
      {reportType==='customer_experience' && frequency==='daily' && <CXDailyDetail sections={sections} />}
      {reportType==='customer_experience' && frequency==='weekly' && <CXWeeklyDetail sections={sections} />}
      {reportType==='customer_experience' && frequency==='monthly' && <CXMonthlyDetail sections={sections} />}
      {reportType==='internal_admin' && frequency==='daily' && <AdminDailyDetail sections={sections} />}
      {reportType==='internal_admin' && frequency==='weekly' && <AdminWeeklyDetail sections={sections} />}
      {reportType==='internal_admin' && frequency==='monthly' && <AdminMonthlyDetail sections={sections} />}
      {reportType==='inventory_officer' && frequency==='daily' && <InventoryDailyDetail sections={sections} />}
      {reportType==='inventory_officer' && frequency==='weekly' && <InventoryWeeklyDetail sections={sections} />}
      {reportType==='inventory_officer' && frequency==='monthly' && <InventoryMonthlyDetail sections={sections} />}

      {report.notes && (
        <div style={{marginTop:16,padding:'12px 16px',background:'#F2EFE7',borderRadius:8}}>
          <div style={{fontSize:9,fontWeight:600,letterSpacing:'.1em',textTransform:'uppercase',color:'#8A8A8A',marginBottom:6}}>Additional Notes</div>
          <div style={{fontSize:13,color:'#3B2A21'}}>{report.notes}</div>
        </div>
      )}
    </div>
  );
}

// Reusable components for report display
const Section = ({ title, children }) => (
  <div style={{marginBottom:20,border:'1px solid var(--border-light)',borderRadius:8,overflow:'hidden'}}>
    <div style={{background:'#F2EFE7',padding:'10px 16px',fontWeight:600,fontSize:13,color:'#556B2F',borderBottom:'1px solid var(--border-light)'}}>{title}</div>
    <div style={{padding:'14px 16px'}}>{children}</div>
  </div>
);

const Field = ({ label, value }) => (
  <div style={{display:'flex',gap:8,marginBottom:8,fontSize:13}}>
    <span style={{color:'#8A8A8A',minWidth:180,flexShrink:0}}>{label}:</span>
    <span style={{color:'#3B2A21',fontWeight:500}}>{value||'—'}</span>
  </div>
);

const TableDisplay = ({ headers, rows }) => (
  <div style={{overflowX:'auto'}}>
    <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
      <thead>
        <tr style={{background:'#556B2F'}}>
          {headers.map(h => <th key={h} style={{padding:'8px 10px',textAlign:'left',color:'#F2EFE7',fontSize:9,textTransform:'uppercase',letterSpacing:'.06em',whiteSpace:'nowrap'}}>{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {(rows||[]).map((row,i) => (
          <tr key={i} style={{background:i%2===0?'#fff':'#F9F9F9'}}>
            {row.map((cell,j) => <td key={j} style={{padding:'8px 10px',borderBottom:'1px solid #EDE7D8',fontSize:12}}>{cell||'—'}</td>)}
          </tr>
        ))}
        {(!rows||rows.length===0) && (
          <tr><td colSpan={headers.length} style={{padding:'12px',textAlign:'center',color:'#8A8A8A',fontSize:12}}>No data entered</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

function SubmitReportView({ user, onBack }) {
  const [reportType, setReportType] = useState('');
  const [frequency, setFrequency] = useState('');
  const [periodLabel, setPeriodLabel] = useState('');
  const [step, setStep] = useState(1);

  const availableTypes = Object.entries(REPORT_TYPES).filter(([,rt]) =>
    rt.roles.includes(user?.role) || ['super_admin','admin'].includes(user?.role)
  );

  const proceed = () => {
    if (!reportType || !frequency || (reportType!=='brand_content_work' && !periodLabel)) {
      alert('Please fill in all fields');
      return;
    }
    setStep(2);
  };

  if (step === 2) {
    const props = { reportType, frequency: frequency.toLowerCase(), periodLabel, onBack, onSubmitted: onBack };
    if(reportType==='brand_content_work') return <ContentWorkReport {...props} />;
    if (reportType==='factory_supervisor' && frequency==='Daily') return <FactoryDailyForm {...props} />;
    if (reportType==='factory_supervisor' && frequency==='Weekly') return <FactoryWeeklyForm {...props} />;
    if (reportType==='factory_supervisor' && frequency==='Monthly') return <FactoryMonthlyForm {...props} />;
    if (reportType==='social_media' && frequency==='Daily') return <SocialDailyForm {...props} />;
    if (reportType==='social_media' && frequency==='Weekly') return <SocialWeeklyForm {...props} />;
    if (reportType==='social_media' && frequency==='Monthly') return <SocialMonthlyForm {...props} />;
    if (reportType==='customer_experience' && frequency==='Daily') return <CXDailyForm {...props} />;
    if (reportType==='customer_experience' && frequency==='Weekly') return <CXWeeklyForm {...props} />;
    if (reportType==='customer_experience' && frequency==='Monthly') return <CXMonthlyForm {...props} />;
    if (reportType==='internal_admin' && frequency==='Daily') return <AdminDailyForm {...props} />;
    if (reportType==='internal_admin' && frequency==='Weekly') return <AdminWeeklyForm {...props} />;
    if (reportType==='internal_admin' && frequency==='Monthly') return <AdminMonthlyForm {...props} />;
    if (reportType==='inventory_officer' && frequency==='Daily') return <InventoryDailyForm {...props} />;
    if (reportType==='inventory_officer' && frequency==='Weekly') return <InventoryWeeklyForm {...props} />;
    if (reportType==='inventory_officer' && frequency==='Monthly') return <InventoryMonthlyForm {...props} />;
  }

  return (
    <div>
      <button className="btn btn-ghost btn-sm" onClick={onBack} style={{marginBottom:16}}>← Back</button>
      <h2 className="page-title" style={{marginBottom:20}}>Submit Report</h2>
      <div style={{maxWidth:500,background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,padding:24}}>
        <div className="form-group">
          <label className="form-label">Report Type *</label>
          <select className="form-control" value={reportType} onChange={e=>setReportType(e.target.value)}>
            <option value="">Select report type</option>
            {availableTypes.map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Frequency *</label>
          <select className="form-control" value={frequency} onChange={e=>setFrequency(e.target.value)}>
            <option value="">Select frequency</option>
            {FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Period {reportType==='brand_content_work'?'(dates are set on the next screen)':'*'}</label>
          <input className="form-control"
            placeholder={frequency==='Daily'?'e.g. Monday 14 July 2026':frequency==='Weekly'?'e.g. Week of 14-18 July 2026':'e.g. July 2026'}
            value={periodLabel} onChange={e=>setPeriodLabel(e.target.value)} />
          <div className="form-hint">Describe the period this report covers</div>
        </div>
        <button className="btn btn-primary" style={{width:'100%',justifyContent:'center'}} onClick={proceed}>
          Continue to Report Form →
        </button>
      </div>
    </div>
  );
}

// Base form component
function BaseReportForm({ title, reportType, frequency, periodLabel, onBack, onSubmitted, children, sections, setSections }) {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      await post('/team-reports', { report_type:reportType, frequency, period_label:periodLabel, sections, notes });
      alert('Report submitted successfully!');
      onSubmitted();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div>
      <button className="btn btn-ghost btn-sm" onClick={onBack} style={{marginBottom:16}}>← Back</button>
      <div style={{background:'#556B2F',borderRadius:10,padding:'14px 20px',marginBottom:20}}>
        <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.2rem',color:'#A8893D',fontWeight:600}}>{title}</div>
        <div style={{fontSize:11,color:'rgba(242,239,231,.5)',marginTop:2}}>{periodLabel}</div>
      </div>
      {error && <div className="alert alert-error mb-md">{error}</div>}
      {children}
      <div className="form-group" style={{marginTop:16}}>
        <label className="form-label">Additional Notes</label>
        <textarea className="form-control" rows={3} placeholder="Any additional information..." value={notes} onChange={e=>setNotes(e.target.value)} />
      </div>
      <div style={{display:'flex',gap:10,marginTop:16}}>
        <button className="btn btn-outline" onClick={onBack}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>
          {saving?'Submitting...':'Submit Report'}
        </button>
      </div>
    </div>
  );
}

// ── FACTORY SUPERVISOR FORMS ─────────────────────────────

function FactoryDailyForm({ reportType, frequency, periodLabel, onBack, onSubmitted }) {
  const [prefilling, setPrefilling] = useState(false);
  const prefill = async () => {
    setPrefilling(true);
    try {
      const data = await get('/team-reports/prefill/factory-daily');
      setS(prev => ({
        ...prev,
        orders_worked: data.orders_worked?.length ? data.orders_worked : prev.orders_worked,
        material_usage: data.material_usage?.length ? data.material_usage : prev.material_usage,
        production_output: data.production_output?.length ? data.production_output : prev.production_output,
      }));
      alert('Data pre-filled! Review and edit before submitting.');
    } catch (err) { alert('Could not prefill: '+err.message); }
    finally { setPrefilling(false); }
  };
  const [s, setS] = useState({
    executive_summary: { major_work:'', major_issue:'', urgent_matter:'', overall_status:'' },
    orders_worked: [{ client:'', product:'', qty_ordered:'', qty_worked:'', stage:'', expected_date:'' }],
    production_output: [{ product:'', qty_completed:'', qty_passed_qc:'', qty_defective:'', qty_reworked:'', qty_ready:'' }],
    staff_attendance: [{ name:'', role:'', present:'Yes', task:'', output:'', note:'' }],
    material_usage: [{ material:'', qty_received:'', qty_used:'', balance:'', order_for:'' }],
    material_shortage: [{ material:'', qty_needed:'', purpose:'', urgency:'', date_needed:'' }],
    qc_report: [{ order:'', defect:'', qty_affected:'', cause:'', correction:'', staff:'' }],
    machine_report: [{ machine:'', status:'Good', issue:'', action:'', need_repair:'No' }],
    cleanliness: [{ area:'Sewing area', status:'Tidy', issue:'', action:'' },
                  { area:'Cutting area', status:'Tidy', issue:'', action:'' },
                  { area:'Packing area', status:'Tidy', issue:'', action:'' },
                  { area:'Material area', status:'Tidy', issue:'', action:'' },
                  { area:'Machine area', status:'Tidy', issue:'', action:'' }],
    pending_orders: [{ client:'', order_no:'', product:'', qty:'', stage:'', pct_complete:'', bottleneck:'', help_needed:'' }],
    tomorrow_plan: [{ order:'', qty_target:'', staff:'', materials:'', expected_output:'' }],
    supervisor_note: ''
  });

  const update = (section, val) => setS(prev => ({...prev, [section]: val}));
  const updateRow = (section, idx, field, val) => {
    const rows = [...s[section]];
    rows[idx] = {...rows[idx], [field]: val};
    update(section, rows);
  };
  const addRow = (section, template) => update(section, [...s[section], {...template}]);
  const removeRow = (section, idx) => update(section, s[section].filter((_,i)=>i!==idx));

  return (
    <BaseReportForm title="Daily Factory Supervisor Report" reportType={reportType} frequency={frequency}
      periodLabel={periodLabel} onBack={onBack} onSubmitted={onSubmitted} sections={s} setSections={setS}>
      <div style={{background:'#E8F5EB',border:'1px solid #B8DEC0',borderRadius:8,padding:'12px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:13,color:'#2E6B3A',fontWeight:500}}>
          📊 Auto-fill from today's job orders and material usage
        </div>
        <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none'}}
          onClick={prefill} disabled={prefilling}>
          {prefilling ? 'Loading...' : '⚡ Pre-fill from BloansBooks'}
        </button>
      </div>

      <Section title="1. Executive Summary of the Day">
        {[['Major work completed today','major_work'],['Major issue noticed today','major_issue'],
          ['Urgent matter requiring attention','urgent_matter'],['Overall factory status','overall_status']].map(([label,key]) => (
          <div className="form-group" key={key}>
            <label className="form-label">{label}</label>
            <textarea className="form-control" rows={2} value={s.executive_summary[key]}
              onChange={e=>update('executive_summary',{...s.executive_summary,[key]:e.target.value})} />
          </div>
        ))}
      </Section>

      <Section title="2. Orders Worked On Today">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>
            {['Client/Order','Product','Qty Ordered','Qty Worked','Stage','Expected Date',''].map(h=>(
              <th key={h} style={{padding:'6px 8px',textAlign:'left',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {s.orders_worked.map((row,i) => (
              <tr key={i}>
                {['client','product','qty_ordered','qty_worked','stage','expected_date'].map(f=>(
                  <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('orders_worked',i,f,e.target.value)} /></td>
                ))}
                <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('orders_worked',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('orders_worked',{client:'',product:'',qty_ordered:'',qty_worked:'',stage:'',expected_date:''})}>+ Add Row</button>
      </Section>

      <Section title="3. Daily Production Output">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>
            {['Product/Order','Completed','Passed QC','Defective','Reworked','Ready',''].map(h=>(
              <th key={h} style={{padding:'6px 8px',textAlign:'left',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {s.production_output.map((row,i) => (
              <tr key={i}>
                {['product','qty_completed','qty_passed_qc','qty_defective','qty_reworked','qty_ready'].map(f=>(
                  <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('production_output',i,f,e.target.value)} /></td>
                ))}
                <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('production_output',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('production_output',{product:'',qty_completed:'',qty_passed_qc:'',qty_defective:'',qty_reworked:'',qty_ready:''})}>+ Add Row</button>
      </Section>

      <Section title="4. Staff Attendance and Work Allocation">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>
            {['Staff Name','Role','Present?','Task','Output','Note',''].map(h=>(
              <th key={h} style={{padding:'6px 8px',textAlign:'left',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {s.staff_attendance.map((row,i) => (
              <tr key={i}>
                {['name','role'].map(f=>(
                  <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('staff_attendance',i,f,e.target.value)} /></td>
                ))}
                <td style={{padding:'4px'}}>
                  <select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.present} onBlur={e=>updateRow('staff_attendance',i,'present',e.target.value)}>
                    <option>Yes</option><option>No</option><option>Late</option>
                  </select>
                </td>
                {['task','output','note'].map(f=>(
                  <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('staff_attendance',i,f,e.target.value)} /></td>
                ))}
                <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('staff_attendance',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('staff_attendance',{name:'',role:'',present:'Yes',task:'',output:'',note:''})}>+ Add Row</button>
      </Section>

      <Section title="5. Material Usage Report">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>
            {['Material','Qty Received','Qty Used','Balance','Order For',''].map(h=>(
              <th key={h} style={{padding:'6px 8px',textAlign:'left',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {s.material_usage.map((row,i) => (
              <tr key={i}>
                {['material','qty_received','qty_used','balance','order_for'].map(f=>(
                  <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('material_usage',i,f,e.target.value)} /></td>
                ))}
                <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('material_usage',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('material_usage',{material:'',qty_received:'',qty_used:'',balance:'',order_for:''})}>+ Add Row</button>
      </Section>

      <Section title="6. QC Report">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>
            {['Order/Product','Defect Found','Qty Affected','Cause','Correction','Staff Responsible',''].map(h=>(
              <th key={h} style={{padding:'6px 8px',textAlign:'left',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {s.qc_report.map((row,i) => (
              <tr key={i}>
                {['order','defect','qty_affected','cause','correction','staff'].map(f=>(
                  <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('qc_report',i,f,e.target.value)} /></td>
                ))}
                <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('qc_report',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('qc_report',{order:'',defect:'',qty_affected:'',cause:'',correction:'',staff:''})}>+ Add Row</button>
      </Section>

      <Section title="7. Machine and Equipment Report">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>
            {['Machine/Equipment','Status','Issue Noticed','Action Taken','Need Repair?',''].map(h=>(
              <th key={h} style={{padding:'6px 8px',textAlign:'left',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {s.machine_report.map((row,i) => (
              <tr key={i}>
                <td style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.machine} onBlur={e=>updateRow('machine_report',i,'machine',e.target.value)} /></td>
                <td style={{padding:'4px'}}>
                  <select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.status} onBlur={e=>updateRow('machine_report',i,'status',e.target.value)}>
                    <option>Good</option><option>Fair</option><option>Poor</option><option>Down</option>
                  </select>
                </td>
                {['issue','action'].map(f=>(
                  <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('machine_report',i,f,e.target.value)} /></td>
                ))}
                <td style={{padding:'4px'}}>
                  <select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.need_repair} onBlur={e=>updateRow('machine_report',i,'need_repair',e.target.value)}>
                    <option>No</option><option>Yes</option>
                  </select>
                </td>
                <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('machine_report',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('machine_report',{machine:'',status:'Good',issue:'',action:'',need_repair:'No'})}>+ Add Row</button>
      </Section>

      <Section title="8. Factory Cleanliness Check">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>
            {['Area','Status','Issue Noticed','Corrective Action'].map(h=>(
              <th key={h} style={{padding:'6px 8px',textAlign:'left',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {s.cleanliness.map((row,i) => (
              <tr key={i}>
                <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.area}</td>
                <td style={{padding:'4px'}}>
                  <select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.status} onBlur={e=>updateRow('cleanliness',i,'status',e.target.value)}>
                    <option>Tidy</option><option>Acceptable</option><option>Untidy</option><option>Needs Attention</option>
                  </select>
                </td>
                {['issue','action'].map(f=>(
                  <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('cleanliness',i,f,e.target.value)} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section title="9. Tomorrow's Production Plan">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>
            {['Order/Task','Qty Target','Staff Assigned','Materials Needed','Expected Output',''].map(h=>(
              <th key={h} style={{padding:'6px 8px',textAlign:'left',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {s.tomorrow_plan.map((row,i) => (
              <tr key={i}>
                {['order','qty_target','staff','materials','expected_output'].map(f=>(
                  <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('tomorrow_plan',i,f,e.target.value)} /></td>
                ))}
                <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('tomorrow_plan',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('tomorrow_plan',{order:'',qty_target:'',staff:'',materials:'',expected_output:''})}>+ Add Row</button>
      </Section>

      <Section title="10. Supervisor's Final Note">
        <textarea className="form-control" rows={4} placeholder="State anything important that management needs to know..."
          value={s.supervisor_note} onChange={e=>update('supervisor_note',e.target.value)} />
      </Section>
    </BaseReportForm>
  );
}

function FactoryDailyDetail({ sections: s }) {
  return (
    <div>
      <Section title="1. Executive Summary">
        <Field label="Major work completed" value={s.executive_summary?.major_work} />
        <Field label="Major issue noticed" value={s.executive_summary?.major_issue} />
        <Field label="Urgent matter" value={s.executive_summary?.urgent_matter} />
        <Field label="Overall status" value={s.executive_summary?.overall_status} />
      </Section>
      <Section title="2. Orders Worked On">
        <TableDisplay headers={['Client/Order','Product','Qty Ordered','Qty Worked','Stage','Expected Date']}
          rows={(s.orders_worked||[]).map(r=>[r.client,r.product,r.qty_ordered,r.qty_worked,r.stage,r.expected_date])} />
      </Section>
      <Section title="3. Production Output">
        <TableDisplay headers={['Product/Order','Completed','Passed QC','Defective','Reworked','Ready']}
          rows={(s.production_output||[]).map(r=>[r.product,r.qty_completed,r.qty_passed_qc,r.qty_defective,r.qty_reworked,r.qty_ready])} />
      </Section>
      <Section title="4. Staff Attendance">
        <TableDisplay headers={['Staff Name','Role','Present','Task','Output','Note']}
          rows={(s.staff_attendance||[]).map(r=>[r.name,r.role,r.present,r.task,r.output,r.note])} />
      </Section>
      <Section title="5. Material Usage">
        <TableDisplay headers={['Material','Qty Received','Qty Used','Balance','Order For']}
          rows={(s.material_usage||[]).map(r=>[r.material,r.qty_received,r.qty_used,r.balance,r.order_for])} />
      </Section>
      <Section title="6. QC Report">
        <TableDisplay headers={['Order/Product','Defect Found','Qty Affected','Cause','Correction','Staff']}
          rows={(s.qc_report||[]).map(r=>[r.order,r.defect,r.qty_affected,r.cause,r.correction,r.staff])} />
      </Section>
      <Section title="7. Machine Report">
        <TableDisplay headers={['Machine','Status','Issue','Action','Need Repair']}
          rows={(s.machine_report||[]).map(r=>[r.machine,r.status,r.issue,r.action,r.need_repair])} />
      </Section>
      <Section title="8. Cleanliness Check">
        <TableDisplay headers={['Area','Status','Issue','Corrective Action']}
          rows={(s.cleanliness||[]).map(r=>[r.area,r.status,r.issue,r.action])} />
      </Section>
      <Section title="9. Tomorrow's Plan">
        <TableDisplay headers={['Order/Task','Qty Target','Staff','Materials','Expected Output']}
          rows={(s.tomorrow_plan||[]).map(r=>[r.order,r.qty_target,r.staff,r.materials,r.expected_output])} />
      </Section>
      {s.supervisor_note && <Section title="10. Supervisor's Note"><p style={{fontSize:13}}>{s.supervisor_note}</p></Section>}
    </div>
  );
}

function FactoryWeeklyForm({ reportType, frequency, periodLabel, onBack, onSubmitted }) {
  const [prefilling, setPrefilling] = useState(false);
  const prefill = async () => {
    setPrefilling(true);
    try {
      const data = await get('/team-reports/prefill/factory-weekly');
      setS(prev => ({...prev, production_output: data.production_output?.length ? data.production_output : prev.production_output, order_tracker: data.order_tracker?.length ? data.order_tracker : prev.order_tracker, material_usage: data.material_usage?.length ? data.material_usage : prev.material_usage}));
      alert('Data pre-filled! Review and edit before submitting.');
    } catch (err) { alert('Could not prefill: '+err.message); }
    finally { setPrefilling(false); }
  };
  const [s, setS] = useState({
    weekly_summary: { achievements:'', problems:'', orders_completed:'', orders_delayed:'', bottleneck:'', support_needed:'' },
    production_output: [{ product:'', qty_produced:'', qty_passed_qc:'', qty_defective:'', qty_delivered:'', notes:'' }],
    order_tracker: [{ client:'', qty_ordered:'', qty_completed:'', qty_pending:'', stage:'', expected:'', delay_reason:'' }],
    staff_performance: [{ name:'', role:'', task:'', performance:'', issue:'', recommendation:'' }],
    attendance: [{ name:'', lateness:'', absence:'', conduct:'', action:'', need_admin:'No' }],
    material_usage: [{ material:'', received:'', used:'', balance:'', shortage:'No', notes:'' }],
    qc_summary: [{ product:'', defect:'', qty_affected:'', cause:'', correction:'', preventive:'' }],
    machine_status: [{ machine:'', status:'Good', issue:'', downtime:'No', repair_needed:'No', recommendation:'' }],
    bottlenecks: [{ bottleneck:'', area:'', effect:'', solution:'', responsible:'' }],
    cleanliness: [{ area:'Sewing area', status:'Good', issue:'', recommendation:'' },
                  { area:'Cutting area', status:'Good', issue:'', recommendation:'' },
                  { area:'Packing area', status:'Good', issue:'', recommendation:'' },
                  { area:'Material area', status:'Good', issue:'', recommendation:'' },
                  { area:'Machine area', status:'Good', issue:'', recommendation:'' }],
    recommendations: ''
  });
  const update = (section, val) => setS(prev => ({...prev, [section]: val}));
  const updateRow = (section, idx, field, val) => { const rows = [...s[section]]; rows[idx] = {...rows[idx], [field]: val}; update(section, rows); };
  const addRow = (section, template) => update(section, [...s[section], {...template}]);
  const removeRow = (section, idx) => update(section, s[section].filter((_,i)=>i!==idx));

  return (
    <BaseReportForm title="Weekly Factory Supervisor Report" reportType={reportType} frequency={frequency}
      periodLabel={periodLabel} onBack={onBack} onSubmitted={onSubmitted} sections={s} setSections={setS}>
      <div style={{background:'#E8F5EB',border:'1px solid #B8DEC0',borderRadius:8,padding:'12px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:13,color:'#2E6B3A',fontWeight:500}}>📊 Auto-fill from BloansBooks data</div>
        <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none'}} onClick={prefill} disabled={prefilling}>
          {prefilling ? 'Loading...' : '⚡ Pre-fill from BloansBooks'}
        </button>
      </div>
      <Section title="1. Weekly Summary">
        {[['Major achievements','achievements'],['Major problems','problems'],['Orders completed','orders_completed'],
          ['Orders delayed','orders_delayed'],['Most serious bottleneck','bottleneck'],['Support needed from management','support_needed']].map(([label,key])=>(
          <div className="form-group" key={key}>
            <label className="form-label">{label}</label>
            <textarea className="form-control" rows={2} value={s.weekly_summary[key]} onChange={e=>update('weekly_summary',{...s.weekly_summary,[key]:e.target.value})} />
          </div>
        ))}
      </Section>
      <Section title="2. Weekly Production Output">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Product','Produced','Passed QC','Defective','Delivered','Notes',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.production_output.map((row,i)=>(
            <tr key={i}>{['product','qty_produced','qty_passed_qc','qty_defective','qty_delivered','notes'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('production_output',i,f,e.target.value)} /></td>
            ))}<td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('production_output',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('production_output',{product:'',qty_produced:'',qty_passed_qc:'',qty_defective:'',qty_delivered:'',notes:''})}>+ Add Row</button>
      </Section>
      <Section title="3. Weekly Order Tracker">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Client','Qty Ordered','Completed','Pending','Stage','Expected','Delay Reason',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.order_tracker.map((row,i)=>(
            <tr key={i}>{['client','qty_ordered','qty_completed','qty_pending','stage','expected','delay_reason'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('order_tracker',i,f,e.target.value)} /></td>
            ))}<td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('order_tracker',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('order_tracker',{client:'',qty_ordered:'',qty_completed:'',qty_pending:'',stage:'',expected:'',delay_reason:''})}>+ Add Row</button>
      </Section>
      <Section title="4. Staff Performance">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Name','Role','Task','Performance','Issue','Recommendation',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.staff_performance.map((row,i)=>(
            <tr key={i}>{['name','role','task','performance','issue','recommendation'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('staff_performance',i,f,e.target.value)} /></td>
            ))}<td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('staff_performance',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('staff_performance',{name:'',role:'',task:'',performance:'',issue:'',recommendation:''})}>+ Add Row</button>
      </Section>
      <Section title="5. Attendance and Discipline">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Staff Name','Lateness','Absence','Conduct Issue','Action Taken','Need Admin Action?',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.attendance.map((row,i)=>(
            <tr key={i}>{['name','lateness','absence','conduct','action'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('attendance',i,f,e.target.value)} /></td>
            ))}
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.need_admin} onBlur={e=>updateRow('attendance',i,'need_admin',e.target.value)}><option>No</option><option>Yes</option></select></td>
            <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('attendance',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('attendance',{name:'',lateness:'',absence:'',conduct:'',action:'',need_admin:'No'})}>+ Add Row</button>
      </Section>
      <Section title="6. Cleanliness / Safety Review">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Area','Status','Issue Noticed','Recommendation'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.cleanliness.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.area}</td>
              <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.status} onBlur={e=>updateRow('cleanliness',i,'status',e.target.value)}><option>Good</option><option>Acceptable</option><option>Poor</option></select></td>
              {['issue','recommendation'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('cleanliness',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="7. Recommendations for Next Week">
        <textarea className="form-control" rows={4} placeholder="List your recommendations..." value={s.recommendations} onChange={e=>update('recommendations',e.target.value)} />
      </Section>
    </BaseReportForm>
  );
}

function FactoryWeeklyDetail({ sections: s }) {
  return (
    <div>
      <Section title="1. Weekly Summary">
        <Field label="Achievements" value={s.weekly_summary?.achievements} />
        <Field label="Problems" value={s.weekly_summary?.problems} />
        <Field label="Orders completed" value={s.weekly_summary?.orders_completed} />
        <Field label="Orders delayed" value={s.weekly_summary?.orders_delayed} />
        <Field label="Bottleneck" value={s.weekly_summary?.bottleneck} />
        <Field label="Support needed" value={s.weekly_summary?.support_needed} />
      </Section>
      <Section title="2. Production Output">
        <TableDisplay headers={['Product','Produced','Passed QC','Defective','Delivered','Notes']}
          rows={(s.production_output||[]).map(r=>[r.product,r.qty_produced,r.qty_passed_qc,r.qty_defective,r.qty_delivered,r.notes])} />
      </Section>
      <Section title="3. Order Tracker">
        <TableDisplay headers={['Client','Qty Ordered','Completed','Pending','Stage','Expected','Delay Reason']}
          rows={(s.order_tracker||[]).map(r=>[r.client,r.qty_ordered,r.qty_completed,r.qty_pending,r.stage,r.expected,r.delay_reason])} />
      </Section>
      <Section title="4. Staff Performance">
        <TableDisplay headers={['Name','Role','Task','Performance','Issue','Recommendation']}
          rows={(s.staff_performance||[]).map(r=>[r.name,r.role,r.task,r.performance,r.issue,r.recommendation])} />
      </Section>
      <Section title="5. Attendance">
        <TableDisplay headers={['Staff Name','Lateness','Absence','Conduct','Action','Need Admin']}
          rows={(s.attendance||[]).map(r=>[r.name,r.lateness,r.absence,r.conduct,r.action,r.need_admin])} />
      </Section>
      <Section title="6. Cleanliness">
        <TableDisplay headers={['Area','Status','Issue','Recommendation']}
          rows={(s.cleanliness||[]).map(r=>[r.area,r.status,r.issue,r.recommendation])} />
      </Section>
      {s.recommendations && <Section title="7. Recommendations"><p style={{fontSize:13}}>{s.recommendations}</p></Section>}
    </div>
  );
}

function FactoryMonthlyForm({ reportType, frequency, periodLabel, onBack, onSubmitted }) {
  const [prefilling, setPrefilling] = useState(false);
  const prefill = async () => {
    setPrefilling(true);
    try {
      const data = await get('/team-reports/prefill/factory-monthly');
      setS(prev => ({...prev, production_output: data.production_output?.length ? data.production_output : prev.production_output, material_usage: data.material_usage?.length ? data.material_usage : prev.material_usage}));
      alert('Data pre-filled! Review and edit before submitting.');
    } catch (err) { alert('Could not prefill: '+err.message); }
    finally { setPrefilling(false); }
  };
  const [s, setS] = useState({
    overview: '',
    production_output: [{ product:'', qty_produced:'', qty_passed_qc:'', qty_defective:'', qty_reworked:'', qty_delivered:'', notes:'' }],
    order_completion: [{ order_no:'', qty_ordered:'', qty_completed:'', qty_pending:'', status:'Completed', delay_reason:'' }],
    staff_performance: [{ name:'', role:'', strength:'', weakness:'', rating:'Excellent', recommendation:'' }],
    attendance: [{ name:'', lateness:'', absence:'', conduct:'', action:'', recommendation:'' }],
    material_usage: [{ material:'', total_received:'', total_used:'', balance:'', wastage:'', notes:'' }],
    qc_review: [{ defect_type:'', occurrences:'', qty_affected:'', cause:'', correction:'', preventive:'' }],
    machine_review: [{ machine:'', performance:'Good', issues:'', repair_done:'', recommendation:'' }],
    bottlenecks: [{ bottleneck:'', frequency:'', effect:'', cause:'', solution:'' }],
    cleanliness: [{ area:'Sewing area', rating:'Excellent', issue:'', action:'', recommendation:'' },
                  { area:'Cutting area', rating:'Excellent', issue:'', action:'', recommendation:'' },
                  { area:'Packing area', rating:'Excellent', issue:'', action:'', recommendation:'' },
                  { area:'Material area', rating:'Excellent', issue:'', action:'', recommendation:'' },
                  { area:'Machine area', rating:'Excellent', issue:'', action:'', recommendation:'' }],
    improvements: [{ recommendation:'', reason:'', benefit:'', priority:'High' }],
    final_note: ''
  });
  const update = (section, val) => setS(prev => ({...prev, [section]: val}));
  const updateRow = (section, idx, field, val) => { const rows = [...s[section]]; rows[idx] = {...rows[idx], [field]: val}; update(section, rows); };
  const addRow = (section, template) => update(section, [...s[section], {...template}]);
  const removeRow = (section, idx) => update(section, s[section].filter((_,i)=>i!==idx));

  return (
    <BaseReportForm title="Monthly Factory Supervisor Report" reportType={reportType} frequency={frequency}
      periodLabel={periodLabel} onBack={onBack} onSubmitted={onSubmitted} sections={s} setSections={setS}>
      <div style={{background:'#E8F5EB',border:'1px solid #B8DEC0',borderRadius:8,padding:'12px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:13,color:'#2E6B3A',fontWeight:500}}>📊 Auto-fill from BloansBooks data</div>
        <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none'}} onClick={prefill} disabled={prefilling}>
          {prefilling ? 'Loading...' : '⚡ Pre-fill from BloansBooks'}
        </button>
      </div>
      <Section title="1. Monthly Factory Overview">
        <textarea className="form-control" rows={5} placeholder="Summarize the factory's performance for the month..." value={s.overview} onChange={e=>update('overview',e.target.value)} />
      </Section>
      <Section title="2. Monthly Production Output">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Product','Produced','Passed QC','Defective','Reworked','Delivered','Notes',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.production_output.map((row,i)=>(
            <tr key={i}>{['product','qty_produced','qty_passed_qc','qty_defective','qty_reworked','qty_delivered','notes'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('production_output',i,f,e.target.value)} /></td>
            ))}<td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('production_output',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('production_output',{product:'',qty_produced:'',qty_passed_qc:'',qty_defective:'',qty_reworked:'',qty_delivered:'',notes:''})}>+ Add Row</button>
      </Section>
      <Section title="3. Staff Performance Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Staff Name','Role','Strength','Weakness/Issue','Rating','Recommendation',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.staff_performance.map((row,i)=>(
            <tr key={i}>{['name','role','strength','weakness'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('staff_performance',i,f,e.target.value)} /></td>
            ))}
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.rating} onBlur={e=>updateRow('staff_performance',i,'rating',e.target.value)}><option>Excellent</option><option>Good</option><option>Fair</option><option>Poor</option></select></td>
            <td style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.recommendation} onBlur={e=>updateRow('staff_performance',i,'recommendation',e.target.value)} /></td>
            <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('staff_performance',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('staff_performance',{name:'',role:'',strength:'',weakness:'',rating:'Excellent',recommendation:''})}>+ Add Row</button>
      </Section>
      <Section title="4. Material Usage Review">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Material','Total Received','Total Used','Balance','Wastage/Loss','Notes',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.material_usage.map((row,i)=>(
            <tr key={i}>{['material','total_received','total_used','balance','wastage','notes'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('material_usage',i,f,e.target.value)} /></td>
            ))}<td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('material_usage',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('material_usage',{material:'',total_received:'',total_used:'',balance:'',wastage:'',notes:''})}>+ Add Row</button>
      </Section>
      <Section title="5. Monthly QC Review">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Defect Type','Occurrences','Qty Affected','Main Cause','Corrective Action','Preventive Action',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.qc_review.map((row,i)=>(
            <tr key={i}>{['defect_type','occurrences','qty_affected','cause','correction','preventive'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('qc_review',i,f,e.target.value)} /></td>
            ))}<td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('qc_review',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('qc_review',{defect_type:'',occurrences:'',qty_affected:'',cause:'',correction:'',preventive:''})}>+ Add Row</button>
      </Section>
      <Section title="6. Improvement Recommendations">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Recommendation','Reason','Expected Benefit','Priority',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.improvements.map((row,i)=>(
            <tr key={i}>{['recommendation','reason','benefit'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('improvements',i,f,e.target.value)} /></td>
            ))}
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.priority} onBlur={e=>updateRow('improvements',i,'priority',e.target.value)}><option>High</option><option>Medium</option><option>Low</option></select></td>
            <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('improvements',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('improvements',{recommendation:'',reason:'',benefit:'',priority:'High'})}>+ Add Row</button>
      </Section>
      <Section title="7. Supervisor's Monthly Final Note">
        <textarea className="form-control" rows={4} placeholder="State the most important thing management should know..." value={s.final_note} onChange={e=>update('final_note',e.target.value)} />
      </Section>
    </BaseReportForm>
  );
}

function FactoryMonthlyDetail({ sections: s }) {
  return (
    <div>
      {s.overview && <Section title="1. Monthly Overview"><p style={{fontSize:13,lineHeight:1.6}}>{s.overview}</p></Section>}
      <Section title="2. Production Output">
        <TableDisplay headers={['Product','Produced','Passed QC','Defective','Reworked','Delivered','Notes']}
          rows={(s.production_output||[]).map(r=>[r.product,r.qty_produced,r.qty_passed_qc,r.qty_defective,r.qty_reworked,r.qty_delivered,r.notes])} />
      </Section>
      <Section title="3. Staff Performance">
        <TableDisplay headers={['Name','Role','Strength','Weakness','Rating','Recommendation']}
          rows={(s.staff_performance||[]).map(r=>[r.name,r.role,r.strength,r.weakness,r.rating,r.recommendation])} />
      </Section>
      <Section title="4. Material Usage">
        <TableDisplay headers={['Material','Total Received','Total Used','Balance','Wastage','Notes']}
          rows={(s.material_usage||[]).map(r=>[r.material,r.total_received,r.total_used,r.balance,r.wastage,r.notes])} />
      </Section>
      <Section title="5. QC Review">
        <TableDisplay headers={['Defect Type','Occurrences','Qty Affected','Cause','Correction','Preventive']}
          rows={(s.qc_review||[]).map(r=>[r.defect_type,r.occurrences,r.qty_affected,r.cause,r.correction,r.preventive])} />
      </Section>
      <Section title="6. Improvement Recommendations">
        <TableDisplay headers={['Recommendation','Reason','Expected Benefit','Priority']}
          rows={(s.improvements||[]).map(r=>[r.recommendation,r.reason,r.benefit,r.priority])} />
      </Section>
      {s.final_note && <Section title="7. Final Note"><p style={{fontSize:13}}>{s.final_note}</p></Section>}
    </div>
  );
}

// ── SOCIAL MEDIA FORMS ───────────────────────────────────

function SocialDailyForm({ reportType, frequency, periodLabel, onBack, onSubmitted }) {
  const [s, setS] = useState({
    content_posted: [{ platform:'Instagram', type:'Reel', caption:'', time_posted:'', screenshot:'No' },
                     { platform:'TikTok', type:'Reel', caption:'', time_posted:'', screenshot:'No' },
                     { platform:'Facebook', type:'Reel', caption:'', time_posted:'', screenshot:'No' },
                     { platform:'WhatsApp Status', type:'Reel', caption:'', time_posted:'', screenshot:'No' }],
    content_pending: [{ type:'', topic:'', status:'', reason:'' }],
    engagement: [{ platform:'Instagram', comments_responded:'', dms_responded:'', questions:'', complaints:'' },
                 { platform:'TikTok', comments_responded:'', dms_responded:'', questions:'', complaints:'' },
                 { platform:'Facebook', comments_responded:'', dms_responded:'', questions:'', complaints:'' },
                 { platform:'WhatsApp Status', comments_responded:'', dms_responded:'', questions:'', complaints:'' }],
    leads: [{ platform:'Instagram', num_leads:'', customer:'', product:'', sent_to_sales:'Yes' },
            { platform:'TikTok', num_leads:'', customer:'', product:'', sent_to_sales:'Yes' },
            { platform:'Facebook', num_leads:'', customer:'', product:'', sent_to_sales:'Yes' },
            { platform:'WhatsApp Status', num_leads:'', customer:'', product:'', sent_to_sales:'Yes' }],
    best_content: { content:'', platform:'', why:'', lesson:'' },
    issues: [{ issue:'', support_needed:'' }],
  });
  const update = (section, val) => setS(prev => ({...prev, [section]: val}));
  const updateRow = (section, idx, field, val) => { const rows = [...s[section]]; rows[idx] = {...rows[idx], [field]: val}; update(section, rows); };
  const addRow = (section, template) => update(section, [...s[section], {...template}]);
  const removeRow = (section, idx) => update(section, s[section].filter((_,i)=>i!==idx));

  return (
    <BaseReportForm title="Daily Social Media / Content Report" reportType={reportType} frequency={frequency}
      periodLabel={periodLabel} onBack={onBack} onSubmitted={onSubmitted} sections={s} setSections={setS}>
      <Section title="1. Content Posted Today">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Platform','Type','Caption/Topic','Time Posted','Screenshot?'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.content_posted.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'4px',fontWeight:500,fontSize:12}}>{row.platform}</td>
              <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.type} onBlur={e=>updateRow('content_posted',i,'type',e.target.value)}><option>Reel</option><option>Post</option><option>Carousel</option><option>Story</option><option>Status</option></select></td>
              <td style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.caption} onBlur={e=>updateRow('content_posted',i,'caption',e.target.value)} /></td>
              <td style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.time_posted} onBlur={e=>updateRow('content_posted',i,'time_posted',e.target.value)} /></td>
              <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.screenshot} onBlur={e=>updateRow('content_posted',i,'screenshot',e.target.value)}><option>Yes</option><option>No</option></select></td>
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="2. Engagement and Community Activity">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Platform','Comments Responded','DMs Responded','Questions Asked','Complaints Escalated'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.engagement.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.platform}</td>
              {['comments_responded','dms_responded','questions','complaints'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('engagement',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="3. Leads Generated From Content">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Platform','Number of Leads','Customer/Handle','Product Asked About','Sent to Sales?'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.leads.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.platform}</td>
              <td style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.num_leads} onBlur={e=>updateRow('leads',i,'num_leads',e.target.value)} /></td>
              <td style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.customer} onBlur={e=>updateRow('leads',i,'customer',e.target.value)} /></td>
              <td style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.product} onBlur={e=>updateRow('leads',i,'product',e.target.value)} /></td>
              <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.sent_to_sales} onBlur={e=>updateRow('leads',i,'sent_to_sales',e.target.value)}><option>Yes</option><option>No</option></select></td>
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="4. Best Performing Content Today">
        {[['Content Title/Topic','content'],['Platform','platform'],['Why it performed well','why'],['Lesson for next content','lesson']].map(([label,key])=>(
          <div className="form-group" key={key}>
            <label className="form-label">{label}</label>
            <input className="form-control" value={s.best_content[key]} onChange={e=>update('best_content',{...s.best_content,[key]:e.target.value})} />
          </div>
        ))}
      </Section>
      <Section title="5. Issues / Requests">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Issue / Request','Support Needed From',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.issues.map((row,i)=>(
            <tr key={i}>
              {['issue','support_needed'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('issues',i,f,e.target.value)} /></td>
              ))}
              <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('issues',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td>
            </tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('issues',{issue:'',support_needed:''})}>+ Add Issue</button>
      </Section>
    </BaseReportForm>
  );
}

function SocialDailyDetail({ sections: s }) {
  return (
    <div>
      <Section title="1. Content Posted">
        <TableDisplay headers={['Platform','Type','Caption/Topic','Time Posted','Screenshot']}
          rows={(s.content_posted||[]).map(r=>[r.platform,r.type,r.caption,r.time_posted,r.screenshot])} />
      </Section>
      <Section title="2. Engagement">
        <TableDisplay headers={['Platform','Comments','DMs','Questions','Complaints']}
          rows={(s.engagement||[]).map(r=>[r.platform,r.comments_responded,r.dms_responded,r.questions,r.complaints])} />
      </Section>
      <Section title="3. Leads Generated">
        <TableDisplay headers={['Platform','Leads','Customer/Handle','Product','Sent to Sales']}
          rows={(s.leads||[]).map(r=>[r.platform,r.num_leads,r.customer,r.product,r.sent_to_sales])} />
      </Section>
      {s.best_content?.content && <Section title="4. Best Content">
        <Field label="Content" value={s.best_content?.content} />
        <Field label="Platform" value={s.best_content?.platform} />
        <Field label="Why it worked" value={s.best_content?.why} />
        <Field label="Lesson" value={s.best_content?.lesson} />
      </Section>}
      {(s.issues||[]).length>0 && <Section title="5. Issues">
        <TableDisplay headers={['Issue','Support Needed From']} rows={(s.issues||[]).map(r=>[r.issue,r.support_needed])} />
      </Section>}
    </div>
  );
}

function SocialWeeklyForm({ reportType, frequency, periodLabel, onBack, onSubmitted }) {
  const [s, setS] = useState({
    content_summary: [
      { platform:'Instagram', posts:'', reels:'', carousels:'', stories:'', status_updates:'' },
      { platform:'TikTok', posts:'', reels:'', carousels:'', stories:'', status_updates:'' },
      { platform:'Facebook', posts:'', reels:'', carousels:'', stories:'', status_updates:'' },
      { platform:'WhatsApp', posts:'', reels:'', carousels:'', stories:'', status_updates:'' }
    ],
    growth: [
      { platform:'Instagram', start_followers:'', end_followers:'', growth:'', reach:'', comments:'', shares:'', saves:'' },
      { platform:'TikTok', start_followers:'', end_followers:'', growth:'', reach:'', comments:'', shares:'', saves:'' },
      { platform:'Facebook', start_followers:'', end_followers:'', growth:'', reach:'', comments:'', shares:'', saves:'' }
    ],
    leads: [
      { platform:'Instagram', leads:'', sent_to_sales:'', closed:'', notes:'' },
      { platform:'TikTok', leads:'', sent_to_sales:'', closed:'', notes:'' },
      { platform:'Facebook', leads:'', sent_to_sales:'', closed:'', notes:'' },
      { platform:'WhatsApp Status', leads:'', sent_to_sales:'', closed:'', notes:'' }
    ],
    best_content: [{ title:'', platform:'', metric:'', repeat:'' }],
    weakest_content: [{ title:'', platform:'', problem:'', correction:'' }],
    content_needed: [{ content:'', person:'', deadline:'', purpose:'' }],
    content_ideas: [{ idea:'', purpose:'', platform:'', priority:'High' }],
    objections: [{ question:'', platform:'', response:'', content_needed:'No' }],
    recommendations: ''
  });
  const update = (section, val) => setS(prev => ({...prev, [section]: val}));
  const updateRow = (section, idx, field, val) => { const rows = [...s[section]]; rows[idx] = {...rows[idx], [field]: val}; update(section, rows); };
  const addRow = (section, template) => update(section, [...s[section], {...template}]);
  const removeRow = (section, idx) => update(section, s[section].filter((_,i)=>i!==idx));

  return (
    <BaseReportForm title="Weekly Social Media / Content Report" reportType={reportType} frequency={frequency}
      periodLabel={periodLabel} onBack={onBack} onSubmitted={onSubmitted} sections={s} setSections={setS}>
      <Section title="1. Weekly Content Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Platform','Posts','Reels/Videos','Carousels','Stories','WhatsApp Status'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.content_summary.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.platform}</td>
              {['posts','reels','carousels','stories','status_updates'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px',width:60}} defaultValue={row[f]} onBlur={e=>updateRow('content_summary',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="2. Growth and Engagement">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Platform','Start Followers','End Followers','Growth','Reach/Views','Comments','Shares','Saves'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.growth.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.platform}</td>
              {['start_followers','end_followers','growth','reach','comments','shares','saves'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px',width:70}} defaultValue={row[f]} onBlur={e=>updateRow('growth',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="3. Leads From Social Media">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Platform','Leads Generated','Sent to Sales','Sales Closed','Notes'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.leads.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.platform}</td>
              {['leads','sent_to_sales','closed','notes'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('leads',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="4. Best Performing Content">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Content Title/Topic','Platform','Metric That Made It Best','What to Repeat',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.best_content.map((row,i)=>(
            <tr key={i}>{['title','platform','metric','repeat'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('best_content',i,f,e.target.value)} /></td>
            ))}<td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('best_content',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('best_content',{title:'',platform:'',metric:'',repeat:''})}>+ Add Row</button>
      </Section>
      <Section title="5. Content Ideas for the Week">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Content Idea','Purpose','Platform','Priority',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.content_ideas.map((row,i)=>(
            <tr key={i}>{['idea','purpose','platform'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('content_ideas',i,f,e.target.value)} /></td>
            ))}
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.priority} onBlur={e=>updateRow('content_ideas',i,'priority',e.target.value)}><option>High</option><option>Medium</option><option>Low</option></select></td>
            <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('content_ideas',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('content_ideas',{idea:'',purpose:'',platform:'',priority:'High'})}>+ Add Row</button>
      </Section>
      <Section title="6. Recommendations for Next Week">
        <textarea className="form-control" rows={4} value={s.recommendations} onChange={e=>update('recommendations',e.target.value)} placeholder="Your recommendations..." />
      </Section>
    </BaseReportForm>
  );
}

function SocialWeeklyDetail({ sections: s }) {
  return (
    <div>
      <Section title="1. Content Summary">
        <TableDisplay headers={['Platform','Posts','Reels','Carousels','Stories','Status Updates']}
          rows={(s.content_summary||[]).map(r=>[r.platform,r.posts,r.reels,r.carousels,r.stories,r.status_updates])} />
      </Section>
      <Section title="2. Growth and Engagement">
        <TableDisplay headers={['Platform','Start','End','Growth','Reach','Comments','Shares','Saves']}
          rows={(s.growth||[]).map(r=>[r.platform,r.start_followers,r.end_followers,r.growth,r.reach,r.comments,r.shares,r.saves])} />
      </Section>
      <Section title="3. Leads">
        <TableDisplay headers={['Platform','Leads','Sent to Sales','Closed','Notes']}
          rows={(s.leads||[]).map(r=>[r.platform,r.leads,r.sent_to_sales,r.closed,r.notes])} />
      </Section>
      <Section title="4. Best Content">
        <TableDisplay headers={['Title','Platform','Metric','Repeat']}
          rows={(s.best_content||[]).map(r=>[r.title,r.platform,r.metric,r.repeat])} />
      </Section>
      <Section title="5. Content Ideas">
        <TableDisplay headers={['Idea','Purpose','Platform','Priority']}
          rows={(s.content_ideas||[]).map(r=>[r.idea,r.purpose,r.platform,r.priority])} />
      </Section>
      {s.recommendations && <Section title="6. Recommendations"><p style={{fontSize:13}}>{s.recommendations}</p></Section>}
    </div>
  );
}

function SocialMonthlyForm({ reportType, frequency, periodLabel, onBack, onSubmitted }) {
  const [s, setS] = useState({
    content_summary: [
      { platform:'Instagram', posts:'', reels:'', carousels:'', stories:'', leads:'', sales_attributed:'' },
      { platform:'TikTok', posts:'', reels:'', carousels:'', stories:'', leads:'', sales_attributed:'' },
      { platform:'Facebook', posts:'', reels:'', carousels:'', stories:'', leads:'', sales_attributed:'' },
      { platform:'WhatsApp Status', posts:'', reels:'', carousels:'', stories:'', leads:'', sales_attributed:'' }
    ],
    growth: [
      { platform:'Instagram', opening:'', closing:'', growth:'', reach:'', engagement:'', notes:'' },
      { platform:'TikTok', opening:'', closing:'', growth:'', reach:'', engagement:'', notes:'' },
      { platform:'Facebook', opening:'', closing:'', growth:'', reach:'', engagement:'', notes:'' }
    ],
    best_content: [{ content:'', platform:'', why:'', sales_generated:'' }],
    weak_content: [{ content:'', platform:'', why_failed:'', correction:'' }],
    objections: [{ question:'', platform:'', response:'', content_idea:'' }],
    brand_visibility: { themes:'', best_response:'', weakest_response:'', needs_visibility:'', testimonials:'', bts_content:'' },
    content_ideas: ['','','','',''],
    support_needed: [{ support:'', from_whom:'', purpose:'' }],
    final_recommendation: { continue:'', stop:'', improve:'', introduce:'' }
  });
  const update = (section, val) => setS(prev => ({...prev, [section]: val}));
  const updateRow = (section, idx, field, val) => { const rows = [...s[section]]; rows[idx] = {...rows[idx], [field]: val}; update(section, rows); };
  const addRow = (section, template) => update(section, [...s[section], {...template}]);
  const removeRow = (section, idx) => update(section, s[section].filter((_,i)=>i!==idx));

  return (
    <BaseReportForm title="Monthly Social Media / Content Report" reportType={reportType} frequency={frequency}
      periodLabel={periodLabel} onBack={onBack} onSubmitted={onSubmitted} sections={s} setSections={setS}>
      <Section title="1. Monthly Content Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Platform','Posts','Reels/Videos','Carousels','Stories','Total Leads','Sales Attributed'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.content_summary.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.platform}</td>
              {['posts','reels','carousels','stories','leads','sales_attributed'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px',width:60}} defaultValue={row[f]} onBlur={e=>updateRow('content_summary',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="2. Monthly Growth Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Platform','Opening Followers','Closing Followers','Growth','Reach/Views','Engagement','Notes'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.growth.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.platform}</td>
              {['opening','closing','growth','reach','engagement','notes'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('growth',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="3. Brand Visibility Review">
        {[['Main content themes used','themes'],['Best audience response','best_response'],
          ['Weakest audience response','weakest_response'],['Products needing more visibility','needs_visibility'],
          ['Customer testimonials/UGC received','testimonials'],['Behind-the-scenes content captured','bts_content']].map(([label,key])=>(
          <div className="form-group" key={key}>
            <label className="form-label">{label}</label>
            <textarea className="form-control" rows={2} value={s.brand_visibility[key]} onChange={e=>update('brand_visibility',{...s.brand_visibility,[key]:e.target.value})} />
          </div>
        ))}
      </Section>
      <Section title="4. Content Ideas for Next Month">
        {s.content_ideas.map((idea,i)=>(
          <div className="form-group" key={i}>
            <label className="form-label">Idea {i+1}</label>
            <input className="form-control" value={idea} onChange={e=>{ const ideas=[...s.content_ideas]; ideas[i]=e.target.value; update('content_ideas',ideas); }} />
          </div>
        ))}
      </Section>
      <Section title="5. Support Needed">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Support Needed','From Whom','Purpose',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.support_needed.map((row,i)=>(
            <tr key={i}>{['support','from_whom','purpose'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('support_needed',i,f,e.target.value)} /></td>
            ))}<td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('support_needed',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('support_needed',{support:'',from_whom:'',purpose:''})}>+ Add Row</button>
      </Section>
      <Section title="6. Final Recommendations">
        {[['Continue','continue'],['Stop','stop'],['Improve','improve'],['Introduce','introduce']].map(([label,key])=>(
          <div className="form-group" key={key}>
            <label className="form-label">{label}</label>
            <textarea className="form-control" rows={2} value={s.final_recommendation[key]} onChange={e=>update('final_recommendation',{...s.final_recommendation,[key]:e.target.value})} />
          </div>
        ))}
      </Section>
    </BaseReportForm>
  );
}

function SocialMonthlyDetail({ sections: s }) {
  return (
    <div>
      <Section title="1. Content Summary">
        <TableDisplay headers={['Platform','Posts','Reels','Carousels','Stories','Leads','Sales Attributed']}
          rows={(s.content_summary||[]).map(r=>[r.platform,r.posts,r.reels,r.carousels,r.stories,r.leads,r.sales_attributed])} />
      </Section>
      <Section title="2. Growth Summary">
        <TableDisplay headers={['Platform','Opening','Closing','Growth','Reach','Engagement','Notes']}
          rows={(s.growth||[]).map(r=>[r.platform,r.opening,r.closing,r.growth,r.reach,r.engagement,r.notes])} />
      </Section>
      {s.brand_visibility && <Section title="3. Brand Visibility">
        <Field label="Content themes" value={s.brand_visibility?.themes} />
        <Field label="Best audience response" value={s.brand_visibility?.best_response} />
        <Field label="Weakest audience response" value={s.brand_visibility?.weakest_response} />
        <Field label="Needs more visibility" value={s.brand_visibility?.needs_visibility} />
        <Field label="Testimonials/UGC" value={s.brand_visibility?.testimonials} />
        <Field label="BTS content captured" value={s.brand_visibility?.bts_content} />
      </Section>}
      {(s.content_ideas||[]).some(i=>i) && <Section title="4. Content Ideas">
        {(s.content_ideas||[]).map((idea,i)=>idea?<div key={i} style={{fontSize:13,marginBottom:6}}>{i+1}. {idea}</div>:null)}
      </Section>}
      {s.final_recommendation && <Section title="5. Final Recommendations">
        <Field label="Continue" value={s.final_recommendation?.continue} />
        <Field label="Stop" value={s.final_recommendation?.stop} />
        <Field label="Improve" value={s.final_recommendation?.improve} />
        <Field label="Introduce" value={s.final_recommendation?.introduce} />
      </Section>}
    </div>
  );
}

// ── CUSTOMER EXPERIENCE FORMS ────────────────────────────

function CXDailyForm({ reportType, frequency, periodLabel, onBack, onSubmitted }) {
  const [prefilling, setPrefilling] = useState(false);
  const prefill = async () => {
    setPrefilling(true);
    try {
      const data = await get('/team-reports/prefill/cx-daily');
      setS(prev => ({...prev, sales_closed: data.sales_closed?.length ? data.sales_closed : prev.sales_closed}));
      alert('Data pre-filled! Review and edit before submitting.');
    } catch (err) { alert('Could not prefill: '+err.message); }
    finally { setPrefilling(false); }
  };
  const [s, setS] = useState({
    enquiry_summary: [
      { channel:'WhatsApp', dms:'', leads:'', closed:'', following_up:'', lost:'' },
      { channel:'Instagram', dms:'', leads:'', closed:'', following_up:'', lost:'' },
      { channel:'Facebook', dms:'', leads:'', closed:'', following_up:'', lost:'' },
      { channel:'TikTok', dms:'', leads:'', closed:'', following_up:'', lost:'' },
      { channel:'Website', dms:'', leads:'', closed:'', following_up:'', lost:'' },
      { channel:'Referral', dms:'', leads:'', closed:'', following_up:'', lost:'' },
      { channel:'Walk In', dms:'', leads:'', closed:'', following_up:'', lost:'' },
    ],
    sales_closed: [{ customer:'', channel:'', product:'', quantity:'', amount_paid:'', balance:'', status:'Paid' }],
    hot_leads: [{ customer:'', channel:'', product:'', last_convo:'', stage:'Price sent', next_followup:'', likelihood:'High' }],
    lost_leads: [{ customer:'', channel:'', product:'', reason:'Price', retarget:'Yes' }],
    complaints: [{ customer:'', channel:'', complaint:'', action:'', status:'Resolved' }],
    objections: [{ question:'', answer:'', content_idea:'No' }],
    followup_check: { all_enquiries:'Yes', hot_leads:'Yes', unpaid_reminded:'Yes', complaints_escalated:'Yes', records_updated:'Yes' },
  });
  const update = (section, val) => setS(prev => ({...prev, [section]: val}));
  const updateRow = (section, idx, field, val) => { const rows = [...s[section]]; rows[idx] = {...rows[idx], [field]: val}; update(section, rows); };
  const addRow = (section, template) => update(section, [...s[section], {...template}]);
  const removeRow = (section, idx) => update(section, s[section].filter((_,i)=>i!==idx));

  const totalEnquiries = s.enquiry_summary.reduce((sum,r)=>sum+parseInt(r.dms||0),0);
  const totalLeads = s.enquiry_summary.reduce((sum,r)=>sum+parseInt(r.leads||0),0);
  const totalClosed = s.enquiry_summary.reduce((sum,r)=>sum+parseInt(r.closed||0),0);
  const conversionRate = totalLeads > 0 ? ((totalClosed/totalLeads)*100).toFixed(1) : 0;

  return (
    <BaseReportForm title="Daily Customer Experience Report" reportType={reportType} frequency={frequency}
      periodLabel={periodLabel} onBack={onBack} onSubmitted={onSubmitted} sections={s} setSections={setS}>
      <div style={{background:'#E8F5EB',border:'1px solid #B8DEC0',borderRadius:8,padding:'12px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:13,color:'#2E6B3A',fontWeight:500}}>📊 Auto-fill from BloansBooks data</div>
        <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none'}} onClick={prefill} disabled={prefilling}>
          {prefilling ? 'Loading...' : '⚡ Pre-fill from BloansBooks'}
        </button>
      </div>
      <Section title="1. Daily Enquiry Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Channel','No. of DMs','No. of Leads','Sales Closed','Still Following Up','Number Lost'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.enquiry_summary.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.channel}</td>
              {['dms','leads','closed','following_up','lost'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input type="number" className="form-control" style={{fontSize:11,padding:'4px 6px',width:60}} defaultValue={row[f]} onBlur={e=>updateRow('enquiry_summary',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}
          <tr style={{background:'#F2EFE7',fontWeight:600}}>
            <td style={{padding:'6px 8px',fontSize:12}}>Total</td>
            <td style={{padding:'6px 8px',fontSize:12}}>{totalEnquiries}</td>
            <td style={{padding:'6px 8px',fontSize:12}}>{totalLeads}</td>
            <td style={{padding:'6px 8px',fontSize:12}}>{totalClosed}</td>
            <td colSpan={2} style={{padding:'6px 8px',fontSize:12}}>Conversion: {conversionRate}%</td>
          </tr>
          </tbody>
        </table>
      </Section>
      <Section title="2. Sales Closed Today">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Customer Name','Channel','Product','Qty','Amount Paid','Balance','Status',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.sales_closed.map((row,i)=>(
            <tr key={i}>{['customer','channel','product','quantity','amount_paid','balance'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('sales_closed',i,f,e.target.value)} /></td>
            ))}
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.status} onBlur={e=>updateRow('sales_closed',i,'status',e.target.value)}><option>Paid</option><option>Part Payment</option><option>Pending</option></select></td>
            <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('sales_closed',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('sales_closed',{customer:'',channel:'',product:'',quantity:'',amount_paid:'',balance:'',status:'Paid'})}>+ Add Sale</button>
      </Section>
      <Section title="3. Hot Leads to Follow Up">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Customer/Handle','Channel','Product','Last Convo','Stage','Next Follow-Up','Likelihood',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.hot_leads.map((row,i)=>(
            <tr key={i}>{['customer','channel','product','last_convo'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('hot_leads',i,f,e.target.value)} /></td>
            ))}
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.stage} onBlur={e=>updateRow('hot_leads',i,'stage',e.target.value)}><option>Price sent</option><option>Awaiting artwork</option><option>Awaiting payment</option><option>Asked for discount</option></select></td>
            <td style={{padding:'4px'}}><input type="date" className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.next_followup} onBlur={e=>updateRow('hot_leads',i,'next_followup',e.target.value)} /></td>
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.likelihood} onBlur={e=>updateRow('hot_leads',i,'likelihood',e.target.value)}><option>High</option><option>Medium</option><option>Low</option></select></td>
            <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('hot_leads',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('hot_leads',{customer:'',channel:'',product:'',last_convo:'',stage:'Price sent',next_followup:'',likelihood:'High'})}>+ Add Lead</button>
      </Section>
      <Section title="4. Lost Leads">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Customer/Handle','Channel','Product Interest','Reason Lost','Retarget Later?',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.lost_leads.map((row,i)=>(
            <tr key={i}>{['customer','channel','product'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('lost_leads',i,f,e.target.value)} /></td>
            ))}
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.reason} onBlur={e=>updateRow('lost_leads',i,'reason',e.target.value)}><option>Price</option><option>Delay</option><option>No response</option><option>Location</option><option>Other</option></select></td>
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.retarget} onBlur={e=>updateRow('lost_leads',i,'retarget',e.target.value)}><option>Yes</option><option>No</option></select></td>
            <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('lost_leads',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('lost_leads',{customer:'',channel:'',product:'',reason:'Price',retarget:'Yes'})}>+ Add Lost Lead</button>
      </Section>
      <Section title="5. Customer Complaints">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Customer','Channel','Complaint','Action Taken','Status',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.complaints.map((row,i)=>(
            <tr key={i}>{['customer','channel','complaint','action'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('complaints',i,f,e.target.value)} /></td>
            ))}
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.status} onBlur={e=>updateRow('complaints',i,'status',e.target.value)}><option>Resolved</option><option>Pending</option><option>Escalated</option></select></td>
            <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('complaints',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('complaints',{customer:'',channel:'',complaint:'',action:'',status:'Resolved'})}>+ Add Complaint</button>
      </Section>
      <Section title="6. Daily Follow-Up Quality Check">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Item','Yes/No','Notes'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{[
            ['Were all enquiries responded to?','all_enquiries'],
            ['Were all hot leads followed up?','hot_leads'],
            ['Were unpaid customers reminded?','unpaid_reminded'],
            ['Were complaints escalated?','complaints_escalated'],
            ['Were customer records updated?','records_updated']
          ].map(([label,key])=>(
            <tr key={key}>
              <td style={{padding:'6px 8px',fontSize:12}}>{label}</td>
              <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px',width:80}} value={s.followup_check[key]} onChange={e=>update('followup_check',{...s.followup_check,[key]:e.target.value})}><option>Yes</option><option>No</option></select></td>
              <td style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} placeholder="Notes..." /></td>
            </tr>
          ))}</tbody>
        </table>
      </Section>
    </BaseReportForm>
  );
}

function CXDailyDetail({ sections: s }) {
  const totalEnquiries = (s.enquiry_summary||[]).reduce((sum,r)=>sum+parseInt(r.dms||0),0);
  const totalLeads = (s.enquiry_summary||[]).reduce((sum,r)=>sum+parseInt(r.leads||0),0);
  const totalClosed = (s.enquiry_summary||[]).reduce((sum,r)=>sum+parseInt(r.closed||0),0);
  return (
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:16}}>
        {[['Total Enquiries',totalEnquiries,'#556B2F'],['Total Leads',totalLeads,'#1E4FAD'],['Sales Closed',totalClosed,'#2E6B3A']].map(([label,val,color])=>(
          <div key={label} style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:8,padding:'12px 14px'}}>
            <div style={{fontSize:9,color:'#8A8A8A',fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>{label}</div>
            <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.5rem',fontWeight:700,color}}>{val}</div>
          </div>
        ))}
      </div>
      <Section title="1. Enquiry Summary">
        <TableDisplay headers={['Channel','DMs','Leads','Closed','Following Up','Lost']}
          rows={(s.enquiry_summary||[]).map(r=>[r.channel,r.dms,r.leads,r.closed,r.following_up,r.lost])} />
      </Section>
      <Section title="2. Sales Closed">
        <TableDisplay headers={['Customer','Channel','Product','Qty','Amount Paid','Balance','Status']}
          rows={(s.sales_closed||[]).map(r=>[r.customer,r.channel,r.product,r.quantity,r.amount_paid,r.balance,r.status])} />
      </Section>
      <Section title="3. Hot Leads">
        <TableDisplay headers={['Customer','Channel','Product','Last Convo','Stage','Next Follow-Up','Likelihood']}
          rows={(s.hot_leads||[]).map(r=>[r.customer,r.channel,r.product,r.last_convo,r.stage,r.next_followup,r.likelihood])} />
      </Section>
      <Section title="4. Lost Leads">
        <TableDisplay headers={['Customer','Channel','Product','Reason','Retarget']}
          rows={(s.lost_leads||[]).map(r=>[r.customer,r.channel,r.product,r.reason,r.retarget])} />
      </Section>
      <Section title="5. Complaints">
        <TableDisplay headers={['Customer','Channel','Complaint','Action','Status']}
          rows={(s.complaints||[]).map(r=>[r.customer,r.channel,r.complaint,r.action,r.status])} />
      </Section>
    </div>
  );
}

function CXWeeklyForm({ reportType, frequency, periodLabel, onBack, onSubmitted }) {
  const [prefilling, setPrefilling] = useState(false);
  const prefill = async () => {
    setPrefilling(true);
    try {
      const data = await get('/team-reports/prefill/cx-weekly');
      setS(prev => ({...prev, top_orders: data.top_orders?.length ? data.top_orders : prev.top_orders}));
      alert('Data pre-filled! Review and edit before submitting.');
    } catch (err) { alert('Could not prefill: '+err.message); }
    finally { setPrefilling(false); }
  };
  const [s, setS] = useState({
    channel_performance: [
      { channel:'WhatsApp', dms:'', leads:'', closed:'', revenue:'', conversion:'', lost:'' },
      { channel:'Instagram', dms:'', leads:'', closed:'', revenue:'', conversion:'', lost:'' },
      { channel:'Facebook', dms:'', leads:'', closed:'', revenue:'', conversion:'', lost:'' },
      { channel:'TikTok', dms:'', leads:'', closed:'', revenue:'', conversion:'', lost:'' },
      { channel:'Website', dms:'', leads:'', closed:'', revenue:'', conversion:'', lost:'' },
      { channel:'Referral', dms:'', leads:'', closed:'', revenue:'', conversion:'', lost:'' },
      { channel:'Walk In', dms:'', leads:'', closed:'', revenue:'', conversion:'', lost:'' },
    ],
    top_orders: [{ customer:'', channel:'', product:'', quantity:'', amount:'', status:'Paid' }],
    followup_list: [{ customer:'', channel:'', product:'', potential:'', next_action:'', date:'' }],
    lost_reasons: [{ reason:'', count:'', solution:'' }],
    complaints: [{ customer:'', channel:'', complaint:'', resolution:'', lesson:'' }],
    observation: '',
    recommendation: ''
  });
  const update = (section, val) => setS(prev => ({...prev, [section]: val}));
  const updateRow = (section, idx, field, val) => { const rows = [...s[section]]; rows[idx] = {...rows[idx], [field]: val}; update(section, rows); };
  const addRow = (section, template) => update(section, [...s[section], {...template}]);
  const removeRow = (section, idx) => update(section, s[section].filter((_,i)=>i!==idx));

  return (
    <BaseReportForm title="Weekly Customer Experience Report" reportType={reportType} frequency={frequency}
      periodLabel={periodLabel} onBack={onBack} onSubmitted={onSubmitted} sections={s} setSections={setS}>
      <div style={{background:'#E8F5EB',border:'1px solid #B8DEC0',borderRadius:8,padding:'12px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:13,color:'#2E6B3A',fontWeight:500}}>📊 Auto-fill from BloansBooks data</div>
        <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none'}} onClick={prefill} disabled={prefilling}>
          {prefilling ? 'Loading...' : '⚡ Pre-fill from BloansBooks'}
        </button>
      </div>
      <Section title="1. Weekly Channel Performance">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Channel','DMs','Leads','Sales Closed','Revenue (₦)','Conversion Rate','Leads Lost'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.channel_performance.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.channel}</td>
              {['dms','leads','closed','revenue','conversion','lost'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px',width:70}} defaultValue={row[f]} onBlur={e=>updateRow('channel_performance',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="2. Top Customers / Orders of the Week">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Customer','Channel','Product','Quantity','Amount','Status',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.top_orders.map((row,i)=>(
            <tr key={i}>{['customer','channel','product','quantity','amount'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('top_orders',i,f,e.target.value)} /></td>
            ))}
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.status} onBlur={e=>updateRow('top_orders',i,'status',e.target.value)}><option>Paid</option><option>Part Payment</option><option>Pending</option></select></td>
            <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('top_orders',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('top_orders',{customer:'',channel:'',product:'',quantity:'',amount:'',status:'Paid'})}>+ Add Row</button>
      </Section>
      <Section title="3. Reasons Customers Did Not Buy">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Reason','Number of Customers','Suggested Solution',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.lost_reasons.map((row,i)=>(
            <tr key={i}>{['reason','count','solution'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('lost_reasons',i,f,e.target.value)} /></td>
            ))}<td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('lost_reasons',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('lost_reasons',{reason:'',count:'',solution:''})}>+ Add Row</button>
      </Section>
      <Section title="4. Sales Behaviour Observation">
        <textarea className="form-control" rows={3} value={s.observation} onChange={e=>update('observation',e.target.value)} placeholder="What patterns did you notice in sales behaviour this week?" />
      </Section>
      <Section title="5. Weekly Recommendation">
        <textarea className="form-control" rows={3} value={s.recommendation} onChange={e=>update('recommendation',e.target.value)} placeholder="What should be improved next week?" />
      </Section>
    </BaseReportForm>
  );
}

function CXWeeklyDetail({ sections: s }) {
  return (
    <div>
      <Section title="1. Channel Performance">
        <TableDisplay headers={['Channel','DMs','Leads','Closed','Revenue','Conversion','Lost']}
          rows={(s.channel_performance||[]).map(r=>[r.channel,r.dms,r.leads,r.closed,r.revenue,r.conversion,r.lost])} />
      </Section>
      <Section title="2. Top Orders">
        <TableDisplay headers={['Customer','Channel','Product','Qty','Amount','Status']}
          rows={(s.top_orders||[]).map(r=>[r.customer,r.channel,r.product,r.quantity,r.amount,r.status])} />
      </Section>
      <Section title="3. Lost Reasons">
        <TableDisplay headers={['Reason','Count','Suggested Solution']}
          rows={(s.lost_reasons||[]).map(r=>[r.reason,r.count,r.solution])} />
      </Section>
      {s.observation && <Section title="4. Behaviour Observation"><p style={{fontSize:13}}>{s.observation}</p></Section>}
      {s.recommendation && <Section title="5. Recommendation"><p style={{fontSize:13}}>{s.recommendation}</p></Section>}
    </div>
  );
}

function CXMonthlyForm({ reportType, frequency, periodLabel, onBack, onSubmitted }) {
  const [prefilling, setPrefilling] = useState(false);
  const prefill = async () => {
    setPrefilling(true);
    try {
      const data = await get('/team-reports/prefill/cx-monthly');
      setS(prev => ({...prev, monthly_metrics: data.metrics ? {...prev.monthly_metrics, ...data.metrics} : prev.monthly_metrics}));
      alert('Data pre-filled! Review and edit before submitting.');
    } catch (err) { alert('Could not prefill: '+err.message); }
    finally { setPrefilling(false); }
  };
  const [s, setS] = useState({
    conversion_dashboard: [
      { channel:'WhatsApp', enquiries:'', leads:'', sales:'', revenue:'', conversion:'' },
      { channel:'Instagram', enquiries:'', leads:'', sales:'', revenue:'', conversion:'' },
      { channel:'TikTok', enquiries:'', leads:'', sales:'', revenue:'', conversion:'' },
      { channel:'Facebook', enquiries:'', leads:'', sales:'', revenue:'', conversion:'' },
      { channel:'Website', enquiries:'', leads:'', sales:'', revenue:'', conversion:'' },
      { channel:'Referral', enquiries:'', leads:'', sales:'', revenue:'', conversion:'' },
      { channel:'Walk-in', enquiries:'', leads:'', sales:'', revenue:'', conversion:'' },
    ],
    monthly_metrics: { total_enquiries:'', total_leads:'', total_sales:'', total_revenue:'', pending_followups:'', total_complaints:'', complaints_resolved:'', complaints_unresolved:'', common_objection:'', best_channel:'', weakest_channel:'' },
    missed_sales: [{ customer:'', channel:'', value:'', reason:'', recoverable:'Yes' }],
    complaint_review: { total_complaints:'', common_complaint:'', avg_response_time:'', major_lesson:'' },
    objection_review: [{ objection:'', count:'', best_response:'', content_needed:'' }],
    channel_quality: [
      { channel:'WhatsApp', quality:'High', reason:'', recommendation:'' },
      { channel:'Instagram', quality:'High', reason:'', recommendation:'' },
      { channel:'TikTok', quality:'High', reason:'', recommendation:'' },
      { channel:'Facebook', quality:'High', reason:'', recommendation:'' },
    ],
    recommendations: ['','','','','']
  });
  const update = (section, val) => setS(prev => ({...prev, [section]: val}));
  const updateRow = (section, idx, field, val) => { const rows = [...s[section]]; rows[idx] = {...rows[idx], [field]: val}; update(section, rows); };
  const addRow = (section, template) => update(section, [...s[section], {...template}]);
  const removeRow = (section, idx) => update(section, s[section].filter((_,i)=>i!==idx));

  return (
    <BaseReportForm title="Monthly Customer Experience Report" reportType={reportType} frequency={frequency}
      periodLabel={periodLabel} onBack={onBack} onSubmitted={onSubmitted} sections={s} setSections={setS}>
      <div style={{background:'#E8F5EB',border:'1px solid #B8DEC0',borderRadius:8,padding:'12px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:13,color:'#2E6B3A',fontWeight:500}}>📊 Auto-fill from BloansBooks data</div>
        <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none'}} onClick={prefill} disabled={prefilling}>
          {prefilling ? 'Loading...' : '⚡ Pre-fill from BloansBooks'}
        </button>
      </div>
      <Section title="1. Monthly Sales Conversion Dashboard">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Channel','Total Enquiries','Total Leads','Total Sales','Revenue (₦)','Conversion Rate'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.conversion_dashboard.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.channel}</td>
              {['enquiries','leads','sales','revenue','conversion'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px',width:80}} defaultValue={row[f]} onBlur={e=>updateRow('conversion_dashboard',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="2. Monthly Customer Experience Review">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {[['Total enquiries','total_enquiries'],['Total leads','total_leads'],['Total sales closed','total_sales'],
            ['Total revenue (₦)','total_revenue'],['Pending follow-ups','pending_followups'],['Total complaints','total_complaints'],
            ['Complaints resolved','complaints_resolved'],['Unresolved complaints','complaints_unresolved'],
            ['Most common objection','common_objection'],['Best sales channel','best_channel'],['Weakest sales channel','weakest_channel']].map(([label,key])=>(
            <div className="form-group" key={key}>
              <label className="form-label" style={{fontSize:11}}>{label}</label>
              <input className="form-control" value={s.monthly_metrics[key]} onChange={e=>update('monthly_metrics',{...s.monthly_metrics,[key]:e.target.value})} />
            </div>
          ))}
        </div>
      </Section>
      <Section title="3. Missed Sales Opportunities">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Customer/Lead','Channel','Estimated Value','Reason Not Closed','Recoverable?',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.missed_sales.map((row,i)=>(
            <tr key={i}>{['customer','channel','value','reason'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('missed_sales',i,f,e.target.value)} /></td>
            ))}
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.recoverable} onBlur={e=>updateRow('missed_sales',i,'recoverable',e.target.value)}><option>Yes</option><option>No</option></select></td>
            <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('missed_sales',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('missed_sales',{customer:'',channel:'',value:'',reason:'',recoverable:'Yes'})}>+ Add Row</button>
      </Section>
      <Section title="4. Sales Objection Review">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Objection','No. of Customers','Best Response Used','Content Needed',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.objection_review.map((row,i)=>(
            <tr key={i}>{['objection','count','best_response','content_needed'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('objection_review',i,f,e.target.value)} /></td>
            ))}<td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('objection_review',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('objection_review',{objection:'',count:'',best_response:'',content_needed:''})}>+ Add Row</button>
      </Section>
      <Section title="5. Recommendations for Next Month">
        {s.recommendations.map((rec,i)=>(
          <div className="form-group" key={i}>
            <label className="form-label">Recommendation {i+1}</label>
            <input className="form-control" value={rec} onChange={e=>{ const recs=[...s.recommendations]; recs[i]=e.target.value; update('recommendations',recs); }} />
          </div>
        ))}
      </Section>
    </BaseReportForm>
  );
}

function CXMonthlyDetail({ sections: s }) {
  return (
    <div>
      <Section title="1. Conversion Dashboard">
        <TableDisplay headers={['Channel','Enquiries','Leads','Sales','Revenue','Conversion']}
          rows={(s.conversion_dashboard||[]).map(r=>[r.channel,r.enquiries,r.leads,r.sales,r.revenue,r.conversion])} />
      </Section>
      <Section title="2. Monthly Metrics">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          {Object.entries(s.monthly_metrics||{}).map(([key,val])=>(
            <Field key={key} label={key.replace(/_/g,' ')} value={val} />
          ))}
        </div>
      </Section>
      <Section title="3. Missed Sales">
        <TableDisplay headers={['Customer','Channel','Value','Reason','Recoverable']}
          rows={(s.missed_sales||[]).map(r=>[r.customer,r.channel,r.value,r.reason,r.recoverable])} />
      </Section>
      <Section title="4. Objection Review">
        <TableDisplay headers={['Objection','Count','Best Response','Content Needed']}
          rows={(s.objection_review||[]).map(r=>[r.objection,r.count,r.best_response,r.content_needed])} />
      </Section>
      {(s.recommendations||[]).some(r=>r) && <Section title="5. Recommendations">
        {(s.recommendations||[]).map((rec,i)=>rec?<div key={i} style={{fontSize:13,marginBottom:6}}>{i+1}. {rec}</div>:null)}
      </Section>}
    </div>
  );
}

// ── INTERNAL ADMIN FORMS ─────────────────────────────────

function AdminDailyForm({ reportType, frequency, periodLabel, onBack, onSubmitted }) {
  const [prefilling, setPrefilling] = useState(false);
  const prefill = async () => {
    setPrefilling(true);
    try {
      const data = await get('/team-reports/prefill/admin-daily');
      setS(prev => ({
        ...prev,
        sales_summary: data.sales_summary ? {...prev.sales_summary, ...data.sales_summary} : prev.sales_summary,
        expenses: data.expenses?.length ? data.expenses : prev.expenses,
        production_summary: data.production_summary?.length ? data.production_summary : prev.production_summary,
        pending_orders: data.pending_orders?.length ? data.pending_orders : prev.pending_orders,
      }));
      alert('Data pre-filled! Review and edit before submitting.');
    } catch (err) { alert('Could not prefill: '+err.message); }
    finally { setPrefilling(false); }
  };
  const [s, setS] = useState({
    executive_summary: { major_work:'', major_issue:'', urgent_matter:'', overall_status:'' },
    sales_summary: { total_sales:'', orders_received:'', paid_orders:'', pending_payments:'', outstanding:'', highest_value_order:'' },
    cx_summary: [
      { channel:'WhatsApp', dms:'', leads:'', closed:'', followup:'', complaints:'' },
      { channel:'Instagram', dms:'', leads:'', closed:'', followup:'', complaints:'' },
      { channel:'TikTok', dms:'', leads:'', closed:'', followup:'', complaints:'' },
      { channel:'Facebook', dms:'', leads:'', closed:'', followup:'', complaints:'' },
      { channel:'Website', dms:'', leads:'', closed:'', followup:'', complaints:'' },
      { channel:'Walk-in', dms:'', leads:'', closed:'', followup:'', complaints:'' },
      { channel:'Referral', dms:'', leads:'', closed:'', followup:'', complaints:'' },
    ],
    social_summary: [
      { platform:'Instagram', content_posted:'', stories:'', dms_responded:'', leads_sent:'', issues:'' },
      { platform:'TikTok', content_posted:'', stories:'', dms_responded:'', leads_sent:'', issues:'' },
      { platform:'Facebook', content_posted:'', stories:'', dms_responded:'', leads_sent:'', issues:'' },
      { platform:'WhatsApp', content_posted:'', stories:'', dms_responded:'', leads_sent:'', issues:'' },
    ],
    expenses: [{ category:'', description:'', amount:'', paid_by:'', receipt:'Yes', logged:'Yes' }],
    fuel: { bought:'Yes', quantity:'', amount:'', purpose:'Generator', logged:'Yes', unusual:'No' },
    production_summary: [{ product:'', qty_produced:'', stage:'', defects:'', note:'' }],
    inventory: [
      { material:'Jute', opening:'', stock_in:'', stock_out:'', closing:'', reorder:'No' },
      { material:'Non-woven fabric', opening:'', stock_in:'', stock_out:'', closing:'', reorder:'No' },
      { material:'Webbing', opening:'', stock_in:'', stock_out:'', closing:'', reorder:'No' },
      { material:'Thread', opening:'', stock_in:'', stock_out:'', closing:'', reorder:'No' },
      { material:'Packaging', opening:'', stock_in:'', stock_out:'', closing:'', reorder:'No' },
    ],
    delivery: [{ client:'', order_no:'', product:'', qty:'', method:'', status:'Delivered', balance:'No' }],
    pending_orders: [{ client:'', order_no:'', product:'', qty:'', stage:'', issue:'', expected:'' }],
    staff_attendance: [{ name:'', issue:'', action:'', followup:'No' }],
    director_attention: '',
  });
  const update = (section, val) => setS(prev => ({...prev, [section]: val}));
  const updateRow = (section, idx, field, val) => { const rows = [...s[section]]; rows[idx] = {...rows[idx], [field]: val}; update(section, rows); };
  const addRow = (section, template) => update(section, [...s[section], {...template}]);
  const removeRow = (section, idx) => update(section, s[section].filter((_,i)=>i!==idx));

  return (
    <BaseReportForm title="Daily Internal Admin Operations Report" reportType={reportType} frequency={frequency}
      periodLabel={periodLabel} onBack={onBack} onSubmitted={onSubmitted} sections={s} setSections={setS}>
      <div style={{background:'#E8F5EB',border:'1px solid #B8DEC0',borderRadius:8,padding:'12px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:13,color:'#2E6B3A',fontWeight:500}}>
          📊 Auto-fill sales, expenses and production from today's data
        </div>
        <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none'}}
          onClick={prefill} disabled={prefilling}>
          {prefilling ? 'Loading...' : '⚡ Pre-fill from BloansBooks'}
        </button>
      </div>
      <Section title="1. Executive Summary">
        {[['Major work completed today','major_work'],['Major issue noticed today','major_issue'],
          ['Urgent matter requiring Director attention','urgent_matter'],['Overall status of operations','overall_status']].map(([label,key])=>(
          <div className="form-group" key={key}>
            <label className="form-label">{label}</label>
            <textarea className="form-control" rows={2} value={s.executive_summary[key]} onChange={e=>update('executive_summary',{...s.executive_summary,[key]:e.target.value})} />
          </div>
        ))}
      </Section>
      <Section title="2. Sales Summary">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {[['Total sales recorded today (₦)','total_sales'],['Number of orders received','orders_received'],
            ['Number of paid orders','paid_orders'],['Number of pending payments','pending_payments'],
            ['Outstanding balances expected (₦)','outstanding'],['Highest-value order of the day','highest_value_order']].map(([label,key])=>(
            <div className="form-group" key={key}>
              <label className="form-label" style={{fontSize:11}}>{label}</label>
              <input className="form-control" value={s.sales_summary[key]} onChange={e=>update('sales_summary',{...s.sales_summary,[key]:e.target.value})} />
            </div>
          ))}
        </div>
      </Section>
      <Section title="3. Customer Experience Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Channel','DMs/Enquiries','Leads','Sales Closed','Pending Follow-Up','Complaints'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.cx_summary.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.channel}</td>
              {['dms','leads','closed','followup','complaints'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px',width:60}} defaultValue={row[f]} onBlur={e=>updateRow('cx_summary',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="4. Social Media and Content Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Platform','Content Posted','Stories','DMs/Comments Responded','Leads Sent to Sales','Issues/Notes'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.social_summary.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.platform}</td>
              {['content_posted','stories','dms_responded','leads_sent','issues'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('social_summary',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="5. Expense Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Category','Description','Amount (₦)','Paid By','Receipt?','Logged?',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.expenses.map((row,i)=>(
            <tr key={i}>{['category','description','amount','paid_by'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('expenses',i,f,e.target.value)} /></td>
            ))}
            {['receipt','logged'].map(f=>(
              <td key={f} style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px',width:60}} defaultValue={row[f]} onBlur={e=>updateRow('expenses',i,f,e.target.value)}><option>Yes</option><option>No</option></select></td>
            ))}
            <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('expenses',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('expenses',{category:'',description:'',amount:'',paid_by:'',receipt:'Yes',logged:'Yes'})}>+ Add Expense</button>
      </Section>
      <Section title="6. Fuel Report">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {[['Fuel bought today?','bought'],['Quantity bought','quantity'],['Amount spent (₦)','amount'],['Purpose','purpose'],['Logged in fuel form?','logged'],['Any unusual fuel usage?','unusual']].map(([label,key])=>(
            <div className="form-group" key={key}>
              <label className="form-label" style={{fontSize:11}}>{label}</label>
              {['bought','logged','unusual'].includes(key)
                ? <select className="form-control" value={s.fuel[key]} onChange={e=>update('fuel',{...s.fuel,[key]:e.target.value})}><option>Yes</option><option>No</option></select>
                : <input className="form-control" value={s.fuel[key]} onChange={e=>update('fuel',{...s.fuel,[key]:e.target.value})} />
              }
            </div>
          ))}
        </div>
      </Section>
      <Section title="7. Production Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Product/Order','Qty Produced Today','Stage Completed','Defects/Errors','Note',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.production_summary.map((row,i)=>(
            <tr key={i}>{['product','qty_produced','stage','defects','note'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('production_summary',i,f,e.target.value)} /></td>
            ))}<td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('production_summary',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('production_summary',{product:'',qty_produced:'',stage:'',defects:'',note:''})}>+ Add Row</button>
      </Section>
      <Section title="8. Inventory Report">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Material','Opening Balance','Stock In','Stock Out/Issued','Closing Balance','Reorder Needed?'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.inventory.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.material}</td>
              {['opening','stock_in','stock_out','closing'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px',width:70}} defaultValue={row[f]} onBlur={e=>updateRow('inventory',i,f,e.target.value)} /></td>
              ))}
              <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px',width:60}} defaultValue={row.reorder} onBlur={e=>updateRow('inventory',i,'reorder',e.target.value)}><option>No</option><option>Yes</option></select></td>
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="9. Delivery and Dispatch Report">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Client','Order No','Product','Qty','Method','Status','Balance Before Dispatch?',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.delivery.map((row,i)=>(
            <tr key={i}>{['client','order_no','product','qty','method'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('delivery',i,f,e.target.value)} /></td>
            ))}
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.status} onBlur={e=>updateRow('delivery',i,'status',e.target.value)}><option>Delivered</option><option>In Transit</option><option>Pending</option></select></td>
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px',width:60}} defaultValue={row.balance} onBlur={e=>updateRow('delivery',i,'balance',e.target.value)}><option>No</option><option>Yes</option></select></td>
            <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('delivery',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('delivery',{client:'',order_no:'',product:'',qty:'',method:'',status:'Delivered',balance:'No'})}>+ Add Row</button>
      </Section>
      <Section title="10. Staff Attendance / Conduct">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Staff Name','Issue/Observation','Action Taken','Needs Follow-Up?',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.staff_attendance.map((row,i)=>(
            <tr key={i}>{['name','issue','action'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('staff_attendance',i,f,e.target.value)} /></td>
            ))}
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px',width:60}} defaultValue={row.followup} onBlur={e=>updateRow('staff_attendance',i,'followup',e.target.value)}><option>No</option><option>Yes</option></select></td>
            <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('staff_attendance',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('staff_attendance',{name:'',issue:'',action:'',followup:'No'})}>+ Add Row</button>
      </Section>
      <Section title="11. Director's Attention Required">
        <textarea className="form-control" rows={4} placeholder="List matters requiring Director's immediate attention, approval, or decision..."
          value={s.director_attention} onChange={e=>update('director_attention',e.target.value)} />
      </Section>
    </BaseReportForm>
  );
}

function AdminDailyDetail({ sections: s }) {
  return (
    <div>
      <Section title="1. Executive Summary">
        <Field label="Major work" value={s.executive_summary?.major_work} />
        <Field label="Major issue" value={s.executive_summary?.major_issue} />
        <Field label="Urgent matter" value={s.executive_summary?.urgent_matter} />
        <Field label="Overall status" value={s.executive_summary?.overall_status} />
      </Section>
      <Section title="2. Sales Summary">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          {Object.entries(s.sales_summary||{}).map(([key,val])=>(
            <Field key={key} label={key.replace(/_/g,' ')} value={val} />
          ))}
        </div>
      </Section>
      <Section title="3. Customer Experience">
        <TableDisplay headers={['Channel','DMs','Leads','Closed','Follow-Up','Complaints']}
          rows={(s.cx_summary||[]).map(r=>[r.channel,r.dms,r.leads,r.closed,r.followup,r.complaints])} />
      </Section>
      <Section title="4. Social Media">
        <TableDisplay headers={['Platform','Content Posted','Stories','DMs Responded','Leads Sent','Issues']}
          rows={(s.social_summary||[]).map(r=>[r.platform,r.content_posted,r.stories,r.dms_responded,r.leads_sent,r.issues])} />
      </Section>
      <Section title="5. Expenses">
        <TableDisplay headers={['Category','Description','Amount','Paid By','Receipt','Logged']}
          rows={(s.expenses||[]).map(r=>[r.category,r.description,r.amount,r.paid_by,r.receipt,r.logged])} />
      </Section>
      <Section title="6. Fuel">
        <Field label="Fuel bought" value={s.fuel?.bought} />
        <Field label="Amount" value={s.fuel?.amount} />
        <Field label="Purpose" value={s.fuel?.purpose} />
        <Field label="Unusual usage" value={s.fuel?.unusual} />
      </Section>
      <Section title="7. Production">
        <TableDisplay headers={['Product/Order','Qty Produced','Stage','Defects','Note']}
          rows={(s.production_summary||[]).map(r=>[r.product,r.qty_produced,r.stage,r.defects,r.note])} />
      </Section>
      <Section title="8. Inventory">
        <TableDisplay headers={['Material','Opening','Stock In','Stock Out','Closing','Reorder']}
          rows={(s.inventory||[]).map(r=>[r.material,r.opening,r.stock_in,r.stock_out,r.closing,r.reorder])} />
      </Section>
      <Section title="9. Delivery">
        <TableDisplay headers={['Client','Order No','Product','Qty','Method','Status','Balance']}
          rows={(s.delivery||[]).map(r=>[r.client,r.order_no,r.product,r.qty,r.method,r.status,r.balance])} />
      </Section>
      <Section title="10. Staff Attendance">
        <TableDisplay headers={['Staff Name','Issue','Action','Follow-Up']}
          rows={(s.staff_attendance||[]).map(r=>[r.name,r.issue,r.action,r.followup])} />
      </Section>
      {s.director_attention && <Section title="11. Director's Attention"><p style={{fontSize:13,color:'#8F2E24',fontWeight:500}}>{s.director_attention}</p></Section>}
    </div>
  );
}

function AdminWeeklyForm({ reportType, frequency, periodLabel, onBack, onSubmitted }) {
  const [prefilling, setPrefilling] = useState(false);
  const prefill = async () => {
    setPrefilling(true);
    try {
      const data = await get('/team-reports/prefill/admin-weekly');
      setS(prev => ({...prev, sales_summary: data.sales_summary ? {...prev.sales_summary, ...data.sales_summary} : prev.sales_summary, expenses: data.expenses ? {...prev.expenses, ...data.expenses} : prev.expenses, production_output: data.production_output?.length ? data.production_output : prev.production_output}));
      alert('Data pre-filled! Review and edit before submitting.');
    } catch (err) { alert('Could not prefill: '+err.message); }
    finally { setPrefilling(false); }
  };
  const [s, setS] = useState({
    executive_summary: { achievements:'', problems:'', orders_completed:'', orders_delayed:'', urgent_decisions:'' },
    sales_summary: { total_sales:'', new_orders:'', completed_orders:'', amount_received:'', outstanding:'', unpaid_customers:'' },
    cx_summary: [
      { channel:'WhatsApp', dms:'', leads:'', closed:'', conversion:'', complaints:'', followups:'' },
      { channel:'Instagram', dms:'', leads:'', closed:'', conversion:'', complaints:'', followups:'' },
      { channel:'TikTok', dms:'', leads:'', closed:'', conversion:'', complaints:'', followups:'' },
      { channel:'Facebook', dms:'', leads:'', closed:'', conversion:'', complaints:'', followups:'' },
      { channel:'Website', dms:'', leads:'', closed:'', conversion:'', complaints:'', followups:'' },
      { channel:'Referral', dms:'', leads:'', closed:'', conversion:'', complaints:'', followups:'' },
      { channel:'Walk-in', dms:'', leads:'', closed:'', conversion:'', complaints:'', followups:'' },
    ],
    social_summary: [
      { platform:'Instagram', posts:'', reels:'', stories:'', engagement:'', leads:'' },
      { platform:'TikTok', posts:'', reels:'', stories:'', engagement:'', leads:'' },
      { platform:'Facebook', posts:'', reels:'', stories:'', engagement:'', leads:'' },
      { platform:'WhatsApp Status', posts:'', reels:'', stories:'', engagement:'', leads:'' },
    ],
    expenses: { raw_materials:'', fuel:'', maintenance:'', logistics:'', staff_related:'', misc:'', total:'' },
    production_output: [{ product:'', qty_produced:'', qty_defective:'', qty_passed_qc:'', notes:'' }],
    inventory: [
      { material:'', opening:'', stock_in:'', issued:'', closing:'', reorder:'No' }
    ],
    pending_orders: [{ client:'', order_no:'', qty:'', stage:'', expected:'', delay:'', action:'' }],
    issues: [{ issue:'', department:'', impact:'', recommendation:'', responsible:'' }],
    director_decisions: ''
  });
  const update = (section, val) => setS(prev => ({...prev, [section]: val}));
  const updateRow = (section, idx, field, val) => { const rows = [...s[section]]; rows[idx] = {...rows[idx], [field]: val}; update(section, rows); };
  const addRow = (section, template) => update(section, [...s[section], {...template}]);
  const removeRow = (section, idx) => update(section, s[section].filter((_,i)=>i!==idx));

  return (
    <BaseReportForm title="Weekly Internal Admin Operations Report" reportType={reportType} frequency={frequency}
      periodLabel={periodLabel} onBack={onBack} onSubmitted={onSubmitted} sections={s} setSections={setS}>
      <div style={{background:'#E8F5EB',border:'1px solid #B8DEC0',borderRadius:8,padding:'12px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:13,color:'#2E6B3A',fontWeight:500}}>📊 Auto-fill from BloansBooks data</div>
        <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none'}} onClick={prefill} disabled={prefilling}>
          {prefilling ? 'Loading...' : '⚡ Pre-fill from BloansBooks'}
        </button>
      </div>
      <Section title="1. Weekly Executive Summary">
        {[['Major achievements','achievements'],['Major problems','problems'],['Orders completed','orders_completed'],['Orders delayed','orders_delayed'],['Urgent decisions needed','urgent_decisions']].map(([label,key])=>(
          <div className="form-group" key={key}>
            <label className="form-label">{label}</label>
            <textarea className="form-control" rows={2} value={s.executive_summary[key]} onChange={e=>update('executive_summary',{...s.executive_summary,[key]:e.target.value})} />
          </div>
        ))}
      </Section>
      <Section title="2. Weekly Sales Summary">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {[['Total sales for the week (₦)','total_sales'],['Number of new orders','new_orders'],['Number of completed orders','completed_orders'],
            ['Total amount received (₦)','amount_received'],['Total outstanding balance (₦)','outstanding'],['Unpaid/pending customers','unpaid_customers']].map(([label,key])=>(
            <div className="form-group" key={key}>
              <label className="form-label" style={{fontSize:11}}>{label}</label>
              <input className="form-control" value={s.sales_summary[key]} onChange={e=>update('sales_summary',{...s.sales_summary,[key]:e.target.value})} />
            </div>
          ))}
        </div>
      </Section>
      <Section title="3. Weekly Customer Experience Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Channel','DMs','Leads','Sales Closed','Conversion Rate','Complaints','Pending Follow-Ups'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.cx_summary.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.channel}</td>
              {['dms','leads','closed','conversion','complaints','followups'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px',width:65}} defaultValue={row[f]} onBlur={e=>updateRow('cx_summary',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="4. Weekly Social Media Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Platform','Posts','Reels/Videos','Stories','Engagement Highlights','Leads Generated'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.social_summary.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.platform}</td>
              {['posts','reels','stories','engagement','leads'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('social_summary',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="5. Weekly Expense Summary">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {[['Raw materials (₦)','raw_materials'],['Fuel (₦)','fuel'],['Maintenance (₦)','maintenance'],
            ['Logistics (₦)','logistics'],['Staff-related expenses (₦)','staff_related'],['Miscellaneous (₦)','misc'],['Total Expenses (₦)','total']].map(([label,key])=>(
            <div className="form-group" key={key}>
              <label className="form-label" style={{fontSize:11}}>{label}</label>
              <input type="number" className="form-control" value={s.expenses[key]} onChange={e=>update('expenses',{...s.expenses,[key]:e.target.value})} />
            </div>
          ))}
        </div>
      </Section>
      <Section title="6. Weekly Production Output">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Product','Qty Produced','Qty Defective','Passed QC','Notes',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.production_output.map((row,i)=>(
            <tr key={i}>{['product','qty_produced','qty_defective','qty_passed_qc','notes'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('production_output',i,f,e.target.value)} /></td>
            ))}<td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('production_output',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('production_output',{product:'',qty_produced:'',qty_defective:'',qty_passed_qc:'',notes:''})}>+ Add Row</button>
      </Section>
      <Section title="7. Weekly Inventory Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Material','Opening Balance','Total Stock In','Total Issued','Closing Balance','Reorder Alert',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.inventory.map((row,i)=>(
            <tr key={i}>{['material','opening','stock_in','issued','closing'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('inventory',i,f,e.target.value)} /></td>
            ))}
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px',width:60}} defaultValue={row.reorder} onBlur={e=>updateRow('inventory',i,'reorder',e.target.value)}><option>No</option><option>Yes</option></select></td>
            <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('inventory',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('inventory',{material:'',opening:'',stock_in:'',issued:'',closing:'',reorder:'No'})}>+ Add Row</button>
      </Section>
      <Section title="8. Issues, Risks and Recommendations">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Issue/Risk','Department','Impact','Recommendation','Responsible',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.issues.map((row,i)=>(
            <tr key={i}>{['issue','department','impact','recommendation','responsible'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('issues',i,f,e.target.value)} /></td>
            ))}<td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('issues',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('issues',{issue:'',department:'',impact:'',recommendation:'',responsible:''})}>+ Add Row</button>
      </Section>
      <Section title="9. Director's Decision Needed">
        <textarea className="form-control" rows={4} placeholder="List decisions needed from the Director..." value={s.director_decisions} onChange={e=>update('director_decisions',e.target.value)} />
      </Section>
    </BaseReportForm>
  );
}

function AdminWeeklyDetail({ sections: s }) {
  return (
    <div>
      <Section title="1. Executive Summary">
        <Field label="Achievements" value={s.executive_summary?.achievements} />
        <Field label="Problems" value={s.executive_summary?.problems} />
        <Field label="Orders completed" value={s.executive_summary?.orders_completed} />
        <Field label="Orders delayed" value={s.executive_summary?.orders_delayed} />
        <Field label="Urgent decisions" value={s.executive_summary?.urgent_decisions} />
      </Section>
      <Section title="2. Sales Summary">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          {Object.entries(s.sales_summary||{}).map(([key,val])=>(
            <Field key={key} label={key.replace(/_/g,' ')} value={val} />
          ))}
        </div>
      </Section>
      <Section title="3. Customer Experience">
        <TableDisplay headers={['Channel','DMs','Leads','Closed','Conversion','Complaints','Follow-Ups']}
          rows={(s.cx_summary||[]).map(r=>[r.channel,r.dms,r.leads,r.closed,r.conversion,r.complaints,r.followups])} />
      </Section>
      <Section title="4. Social Media">
        <TableDisplay headers={['Platform','Posts','Reels','Stories','Engagement','Leads']}
          rows={(s.social_summary||[]).map(r=>[r.platform,r.posts,r.reels,r.stories,r.engagement,r.leads])} />
      </Section>
      <Section title="5. Expenses">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          {Object.entries(s.expenses||{}).map(([key,val])=>(
            <Field key={key} label={key.replace(/_/g,' ')} value={val} />
          ))}
        </div>
      </Section>
      <Section title="6. Production Output">
        <TableDisplay headers={['Product','Produced','Defective','Passed QC','Notes']}
          rows={(s.production_output||[]).map(r=>[r.product,r.qty_produced,r.qty_defective,r.qty_passed_qc,r.notes])} />
      </Section>
      <Section title="7. Inventory">
        <TableDisplay headers={['Material','Opening','Stock In','Issued','Closing','Reorder']}
          rows={(s.inventory||[]).map(r=>[r.material,r.opening,r.stock_in,r.issued,r.closing,r.reorder])} />
      </Section>
      <Section title="8. Issues and Risks">
        <TableDisplay headers={['Issue','Department','Impact','Recommendation','Responsible']}
          rows={(s.issues||[]).map(r=>[r.issue,r.department,r.impact,r.recommendation,r.responsible])} />
      </Section>
      {s.director_decisions && <Section title="9. Director's Decision Needed"><p style={{fontSize:13,color:'#8F2E24',fontWeight:500}}>{s.director_decisions}</p></Section>}
    </div>
  );
}

function AdminMonthlyForm({ reportType, frequency, periodLabel, onBack, onSubmitted }) {
  const [prefilling, setPrefilling] = useState(false);
  const prefill = async () => {
    setPrefilling(true);
    try {
      const data = await get('/team-reports/prefill/admin-monthly');
      setS(prev => ({...prev, sales_summary: data.sales_summary ? {...prev.sales_summary, ...data.sales_summary} : prev.sales_summary, expense_summary: data.expense_summary ? {...prev.expense_summary, ...data.expense_summary} : prev.expense_summary, production_summary: data.production_summary?.length ? data.production_summary : prev.production_summary, inventory_summary: data.inventory_summary?.length ? data.inventory_summary : prev.inventory_summary}));
      alert('Data pre-filled! Review and edit before submitting.');
    } catch (err) { alert('Could not prefill: '+err.message); }
    finally { setPrefilling(false); }
  };
  const [s, setS] = useState({
    overview: '',
    sales_summary: { total_sales:'', amount_received:'', outstanding:'', orders_received:'', orders_completed:'', orders_pending:'', best_channel:'', worst_channel:'' },
    sales_by_channel: [
      { channel:'WhatsApp', leads:'', sales:'', revenue:'' },
      { channel:'Instagram', leads:'', sales:'', revenue:'' },
      { channel:'TikTok', leads:'', sales:'', revenue:'' },
      { channel:'Facebook', leads:'', sales:'', revenue:'' },
      { channel:'Website', leads:'', sales:'', revenue:'' },
      { channel:'Referral', leads:'', sales:'', revenue:'' },
      { channel:'Walk-in', leads:'', sales:'', revenue:'' },
    ],
    expense_summary: { raw_materials:'', marketing:'', misc:'', salaries:'', maintenance:'', logistics:'', fuel:'', total:'' },
    fuel_summary: { total_spent:'', num_purchases:'', avg_weekly:'', highest_week:'', unusual:'No', recommendation:'' },
    production_summary: [{ product:'', qty_produced:'', qty_delivered:'', qty_pending:'', qty_defective:'', notes:'' }],
    inventory_summary: [{ material:'', opening:'', purchased:'', issued:'', closing:'', variance:'', reorder:'No' }],
    social_summary: [
      { platform:'Instagram', posts:'', reels:'', stories:'', followers_gained:'', leads:'', sales:'', best_content:'' },
      { platform:'TikTok', posts:'', reels:'', stories:'', followers_gained:'', leads:'', sales:'', best_content:'' },
      { platform:'Facebook', posts:'', reels:'', stories:'', followers_gained:'', leads:'', sales:'', best_content:'' },
      { platform:'WhatsApp Status', posts:'', reels:'', stories:'', followers_gained:'', leads:'', sales:'', best_content:'' },
    ],
    cx_summary: { total_dms:'', total_leads:'', total_sales:'', conversion_rate:'', complaints_received:'', complaints_resolved:'', unresolved:'', common_objection:'', followup_opportunities:'' },
    hr_summary: { attendance_issues:'', lateness_issues:'', disciplinary:'', performance_concerns:'', commendations:'', policies_enforced:'' },
    challenges: [{ challenge:'', effect:'', solution:'', responsible:'' }],
    recommendations: ['','','','','']
  });
  const update = (section, val) => setS(prev => ({...prev, [section]: val}));
  const updateRow = (section, idx, field, val) => { const rows = [...s[section]]; rows[idx] = {...rows[idx], [field]: val}; update(section, rows); };
  const addRow = (section, template) => update(section, [...s[section], {...template}]);
  const removeRow = (section, idx) => update(section, s[section].filter((_,i)=>i!==idx));

  return (
    <BaseReportForm title="Monthly Internal Admin Operations Report" reportType={reportType} frequency={frequency}
      periodLabel={periodLabel} onBack={onBack} onSubmitted={onSubmitted} sections={s} setSections={setS}>
      <div style={{background:'#E8F5EB',border:'1px solid #B8DEC0',borderRadius:8,padding:'12px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:13,color:'#2E6B3A',fontWeight:500}}>📊 Auto-fill from BloansBooks data</div>
        <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none'}} onClick={prefill} disabled={prefilling}>
          {prefilling ? 'Loading...' : '⚡ Pre-fill from BloansBooks'}
        </button>
      </div>
      <Section title="1. Monthly Overview">
        <textarea className="form-control" rows={5} placeholder="Give a clear summary of the month..." value={s.overview} onChange={e=>update('overview',e.target.value)} />
      </Section>
      <Section title="2. Monthly Sales Summary">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {[['Total sales recorded (₦)','total_sales'],['Total amount received (₦)','amount_received'],['Total outstanding (₦)','outstanding'],
            ['Orders received','orders_received'],['Orders completed','orders_completed'],['Orders pending','orders_pending'],
            ['Highest sales channel','best_channel'],['Lowest sales channel','worst_channel']].map(([label,key])=>(
            <div className="form-group" key={key}>
              <label className="form-label" style={{fontSize:11}}>{label}</label>
              <input className="form-control" value={s.sales_summary[key]} onChange={e=>update('sales_summary',{...s.sales_summary,[key]:e.target.value})} />
            </div>
          ))}
        </div>
      </Section>
      <Section title="3. Monthly Sales by Channel">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Channel','Leads','Sales','Revenue (₦)'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.sales_by_channel.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.channel}</td>
              {['leads','sales','revenue'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('sales_by_channel',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="4. Monthly Expense Summary">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {[['Raw materials (₦)','raw_materials'],['Marketing/Ads (₦)','marketing'],['Miscellaneous (₦)','misc'],
            ['Salaries/Staff (₦)','salaries'],['Maintenance (₦)','maintenance'],['Logistics (₦)','logistics'],
            ['Fuel (₦)','fuel'],['Total Expenses (₦)','total']].map(([label,key])=>(
            <div className="form-group" key={key}>
              <label className="form-label" style={{fontSize:11}}>{label}</label>
              <input type="number" className="form-control" value={s.expense_summary[key]} onChange={e=>update('expense_summary',{...s.expense_summary,[key]:e.target.value})} />
            </div>
          ))}
        </div>
      </Section>
      <Section title="5. Monthly Production Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Product','Qty Produced','Qty Delivered','Qty Pending','Qty Defective','Notes',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.production_summary.map((row,i)=>(
            <tr key={i}>{['product','qty_produced','qty_delivered','qty_pending','qty_defective','notes'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('production_summary',i,f,e.target.value)} /></td>
            ))}<td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('production_summary',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('production_summary',{product:'',qty_produced:'',qty_delivered:'',qty_pending:'',qty_defective:'',notes:''})}>+ Add Row</button>
      </Section>
      <Section title="6. Monthly Inventory Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Material','Opening','Purchased','Issued','Closing','Variance/Loss','Reorder?',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.inventory_summary.map((row,i)=>(
            <tr key={i}>{['material','opening','purchased','issued','closing','variance'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('inventory_summary',i,f,e.target.value)} /></td>
            ))}
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px',width:60}} defaultValue={row.reorder} onBlur={e=>updateRow('inventory_summary',i,'reorder',e.target.value)}><option>No</option><option>Yes</option></select></td>
            <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('inventory_summary',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('inventory_summary',{material:'',opening:'',purchased:'',issued:'',closing:'',variance:'',reorder:'No'})}>+ Add Row</button>
      </Section>
      <Section title="7. Monthly Social Media Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Platform','Posts','Reels','Stories','Followers Gained','Leads','Sales','Best Content'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.social_summary.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'6px 8px',fontWeight:500,fontSize:12}}>{row.platform}</td>
              {['posts','reels','stories','followers_gained','leads','sales','best_content'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('social_summary',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </Section>
      <Section title="8. Monthly Customer Experience Summary">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {[['Total DMs/enquiries','total_dms'],['Total leads','total_leads'],['Total sales closed','total_sales'],
            ['Overall conversion rate','conversion_rate'],['Complaints received','complaints_received'],['Complaints resolved','complaints_resolved'],
            ['Unresolved complaints','unresolved'],['Common customer objections','common_objection'],['Follow-up opportunities','followup_opportunities']].map(([label,key])=>(
            <div className="form-group" key={key}>
              <label className="form-label" style={{fontSize:11}}>{label}</label>
              <input className="form-control" value={s.cx_summary[key]} onChange={e=>update('cx_summary',{...s.cx_summary,[key]:e.target.value})} />
            </div>
          ))}
        </div>
      </Section>
      <Section title="9. Staff / HR / Compliance Summary">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {[['Attendance issues','attendance_issues'],['Lateness issues','lateness_issues'],['Disciplinary issues','disciplinary'],
            ['Performance concerns','performance_concerns'],['Staff commendations','commendations'],['Policies/SOPs enforced','policies_enforced']].map(([label,key])=>(
            <div className="form-group" key={key}>
              <label className="form-label" style={{fontSize:11}}>{label}</label>
              <textarea className="form-control" rows={2} value={s.hr_summary[key]} onChange={e=>update('hr_summary',{...s.hr_summary,[key]:e.target.value})} />
            </div>
          ))}
        </div>
      </Section>
      <Section title="10. Monthly Challenges">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Challenge','Effect on Business','Suggested Solution','Responsible Person',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.challenges.map((row,i)=>(
            <tr key={i}>{['challenge','effect','solution','responsible'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('challenges',i,f,e.target.value)} /></td>
            ))}<td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('challenges',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('challenges',{challenge:'',effect:'',solution:'',responsible:''})}>+ Add Row</button>
      </Section>
      <Section title="11. Recommendations for Next Month">
        {s.recommendations.map((rec,i)=>(
          <div className="form-group" key={i}>
            <label className="form-label">Recommendation {i+1}</label>
            <input className="form-control" value={rec} onChange={e=>{ const recs=[...s.recommendations]; recs[i]=e.target.value; update('recommendations',recs); }} />
          </div>
        ))}
      </Section>
    </BaseReportForm>
  );
}

function AdminMonthlyDetail({ sections: s }) {
  return (
    <div>
      {s.overview && <Section title="1. Monthly Overview"><p style={{fontSize:13,lineHeight:1.6}}>{s.overview}</p></Section>}
      <Section title="2. Sales Summary">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          {Object.entries(s.sales_summary||{}).map(([key,val])=>(
            <Field key={key} label={key.replace(/_/g,' ')} value={val} />
          ))}
        </div>
      </Section>
      <Section title="3. Sales by Channel">
        <TableDisplay headers={['Channel','Leads','Sales','Revenue']}
          rows={(s.sales_by_channel||[]).map(r=>[r.channel,r.leads,r.sales,r.revenue])} />
      </Section>
      <Section title="4. Expense Summary">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          {Object.entries(s.expense_summary||{}).map(([key,val])=>(
            <Field key={key} label={key.replace(/_/g,' ')} value={val} />
          ))}
        </div>
      </Section>
      <Section title="5. Production Summary">
        <TableDisplay headers={['Product','Produced','Delivered','Pending','Defective','Notes']}
          rows={(s.production_summary||[]).map(r=>[r.product,r.qty_produced,r.qty_delivered,r.qty_pending,r.qty_defective,r.notes])} />
      </Section>
      <Section title="6. Inventory Summary">
        <TableDisplay headers={['Material','Opening','Purchased','Issued','Closing','Variance','Reorder']}
          rows={(s.inventory_summary||[]).map(r=>[r.material,r.opening,r.purchased,r.issued,r.closing,r.variance,r.reorder])} />
      </Section>
      <Section title="7. Social Media">
        <TableDisplay headers={['Platform','Posts','Reels','Stories','Followers Gained','Leads','Sales','Best Content']}
          rows={(s.social_summary||[]).map(r=>[r.platform,r.posts,r.reels,r.stories,r.followers_gained,r.leads,r.sales,r.best_content])} />
      </Section>
      <Section title="8. Customer Experience">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          {Object.entries(s.cx_summary||{}).map(([key,val])=>(
            <Field key={key} label={key.replace(/_/g,' ')} value={val} />
          ))}
        </div>
      </Section>
      <Section title="9. HR Summary">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          {Object.entries(s.hr_summary||{}).map(([key,val])=>(
            <Field key={key} label={key.replace(/_/g,' ')} value={val} />
          ))}
        </div>
      </Section>
      <Section title="10. Challenges">
        <TableDisplay headers={['Challenge','Effect','Solution','Responsible']}
          rows={(s.challenges||[]).map(r=>[r.challenge,r.effect,r.solution,r.responsible])} />
      </Section>
      {(s.recommendations||[]).some(r=>r) && <Section title="11. Recommendations">
        {(s.recommendations||[]).map((rec,i)=>rec?<div key={i} style={{fontSize:13,marginBottom:6}}>{i+1}. {rec}</div>:null)}
      </Section>}
    </div>
  );
}


// ── INVENTORY OFFICER FORMS ──────────────────────────────

function InventoryTableForm({ title, section, rows, headers, fields, template, dropdowns={}, onUpdateRow, onAddRow, onRemoveRow, s }) {
  const actualRows = rows || (s && s[section]) || [];
  return (
    <Section title={title}>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
        <thead><tr style={{background:'#F2EFE7'}}>
          {[...headers,''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}
        </tr></thead>
        <tbody>{actualRows.map((row,i)=>(
          <tr key={i}>{fields.map(f=>(
            <td key={f} style={{padding:'4px'}}>
              {dropdowns[f]
                ? <select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>onUpdateRow(section,i,f,e.target.value)}>
                    {dropdowns[f].map(o=><option key={o}>{o}</option>)}
                  </select>
                : <input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>onUpdateRow(section,i,f,e.target.value)} />
              }
            </td>
          ))}<td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" onClick={()=>onRemoveRow(section,i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
        ))}</tbody>
      </table>
      <button className="btn btn-outline btn-sm" onClick={()=>onAddRow(section,template)}>+ Add Row</button>
    </Section>
  );
}

function InventoryDailyForm({ reportType, frequency, periodLabel, onBack, onSubmitted }) {
  const [prefilling, setPrefilling] = useState(false);
  const prefill = async () => {
    setPrefilling(true);
    try {
      const data = await get('/team-reports/prefill/inventory-daily');
      setS(prev => ({
        ...prev,
        materials_issued: data.materials_issued?.length ? data.materials_issued : prev.materials_issued,
        low_stock: data.low_stock?.length ? data.low_stock : prev.low_stock,
        finished_goods_received: data.finished_goods_received?.length ? data.finished_goods_received : prev.finished_goods_received,
        finished_goods_released: data.finished_goods_released?.length ? data.finished_goods_released : prev.finished_goods_released,
        raw_materials_received: data.raw_materials_received?.length ? data.raw_materials_received : prev.raw_materials_received,
        kpis: { ...prev.kpis, ...data.kpis }
      }));
      alert('Data pre-filled from BloansBooks! Review and edit before submitting.');
    } catch (err) { alert('Could not prefill: '+err.message); }
    finally { setPrefilling(false); }
  };
  const [s, setS] = useState({
    operational_summary: { materials_received:'', materials_prepared:'', materials_issued:'', returns_received:'', finished_goods_received:'', finished_goods_released:'', major_challenges:'' },
    raw_materials_received: [{ material:'', unit:'', quantity:'', received_from:'', reference:'', condition:'Good', remarks:'' }],
    materials_prepared: [{ material:'', quantity_prepared:'', job_order:'', prepared_by:'', remarks:'' }],
    materials_issued: [{ job_order:'', production_staff:'', material:'', quantity:'', time_issued:'', remarks:'' }],
    materials_returned: [{ job_order:'', staff:'', item:'', expected_qty:'', actual_qty:'', difference:'', remarks:'' }],
    finished_goods_received: [{ job_order:'', product:'', expected_qty:'', actual_qty:'', qc_status:'Pass', remarks:'' }],
    finished_goods_released: [{ order_no:'', customer:'', product:'', quantity:'', released_to:'', reference:'' }],
    variances: [{ item:'', expected:'', actual:'', difference:'', investigation:'', action_taken:'' }],
    damaged: [{ item:'', quantity:'', cause:'', action:'' }],
    low_stock: [{ material:'', current_balance:'', minimum_level:'', recommended_action:'' }],
    challenges: '',
    actions_taken: '',
    tomorrows_priorities: { materials_to_prepare:'', job_orders:'', stock_counts:'', procurement:'', followups:'' },
    kpis: { materials_received:'', materials_prepared:'', materials_issued:'', returns_verified:'', finished_goods_received:'', finished_goods_released:'', variances:'', low_stock_items:'' }
  });
  const update = (section, val) => setS(prev => ({...prev, [section]: val}));
  const updateRow = (section, idx, field, val) => { const rows = [...s[section]]; rows[idx] = {...rows[idx], [field]: val}; update(section, rows); };
  const addRow = (section, template) => update(section, [...s[section], {...template}]);
  const removeRow = (section, idx) => update(section, s[section].filter((_,i)=>i!==idx));

  // TableForm moved to module level to prevent re-render focus loss

  return (
    <BaseReportForm title="Daily Inventory Report" reportType={reportType} frequency={frequency}
      periodLabel={periodLabel} onBack={onBack} onSubmitted={onSubmitted} sections={s} setSections={setS}>
      <div style={{background:'#E8F5EB',border:'1px solid #B8DEC0',borderRadius:8,padding:'12px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:13,color:'#2E6B3A',fontWeight:500}}>
          📊 Auto-fill sections from today's BloansBooks data
        </div>
        <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none'}}
          onClick={prefill} disabled={prefilling}>
          {prefilling ? 'Loading...' : '⚡ Pre-fill from BloansBooks'}
        </button>
      </div>

      <Section title="Section A – Daily Operational Summary">
        {[['Materials received','materials_received'],['Materials prepared','materials_prepared'],
          ['Materials issued','materials_issued'],['Returns received','returns_received'],
          ['Finished goods received','finished_goods_received'],['Finished goods released','finished_goods_released'],
          ['Major challenges','major_challenges']].map(([label,key])=>(
          <div className="form-group" key={key}>
            <label className="form-label">{label}</label>
            <textarea className="form-control" rows={2} value={s.operational_summary[key]}
              onChange={e=>update('operational_summary',{...s.operational_summary,[key]:e.target.value})} />
          </div>
        ))}
      </Section>

      <InventoryTableForm title="Section B – Raw Materials Received"
        section="raw_materials_received"
        headers={['Material','Unit','Quantity','Received From','Reference','Condition','Remarks']}
        fields={['material','unit','quantity','received_from','reference','condition','remarks']}
        template={{material:'',unit:'',quantity:'',received_from:'',reference:'',condition:'Good',remarks:''}}
        dropdowns={{condition:['Good','Damaged','Partial']}} onUpdateRow={updateRow} onAddRow={addRow} onRemoveRow={removeRow} s={s} />

      <InventoryTableForm title="Section C – Materials Prepared"
        section="materials_prepared"
        headers={['Material','Quantity Prepared','Job Order','Prepared By','Remarks']}
        fields={['material','quantity_prepared','job_order','prepared_by','remarks']}
        template={{material:'',quantity_prepared:'',job_order:'',prepared_by:'',remarks:''}} onUpdateRow={updateRow} onAddRow={addRow} onRemoveRow={removeRow} s={s} />

      <InventoryTableForm title="Section D – Materials Issued"
        section="materials_issued"
        headers={['Job Order','Production Staff','Material','Quantity','Time Issued','Remarks']}
        fields={['job_order','production_staff','material','quantity','time_issued','remarks']}
        template={{job_order:'',production_staff:'',material:'',quantity:'',time_issued:'',remarks:''}} onUpdateRow={updateRow} onAddRow={addRow} onRemoveRow={removeRow} s={s} />

      <InventoryTableForm title="Section E – Materials Returned"
        section="materials_returned"
        headers={['Job Order','Staff','Item Returned','Expected Qty','Actual Qty','Difference','Remarks']}
        fields={['job_order','staff','item','expected_qty','actual_qty','difference','remarks']}
        template={{job_order:'',staff:'',item:'',expected_qty:'',actual_qty:'',difference:'',remarks:''}} onUpdateRow={updateRow} onAddRow={addRow} onRemoveRow={removeRow} s={s} />

      <InventoryTableForm title="Section F – Finished Goods Received"
        section="finished_goods_received"
        headers={['Job Order','Product','Expected Qty','Actual Qty','QC Status','Remarks']}
        fields={['job_order','product','expected_qty','actual_qty','qc_status','remarks']}
        template={{job_order:'',product:'',expected_qty:'',actual_qty:'',qc_status:'Pass',remarks:''}}
        dropdowns={{qc_status:['Pass','Fail','Partial']}} onUpdateRow={updateRow} onAddRow={addRow} onRemoveRow={removeRow} s={s} />

      <InventoryTableForm title="Section G – Finished Goods Released"
        section="finished_goods_released"
        headers={['Order No.','Customer','Product','Quantity','Released To','Reference']}
        fields={['order_no','customer','product','quantity','released_to','reference']}
        template={{order_no:'',customer:'',product:'',quantity:'',released_to:'',reference:''}} onUpdateRow={updateRow} onAddRow={addRow} onRemoveRow={removeRow} s={s} />

      <InventoryTableForm title="Section H – Inventory Variances"
        section="variances"
        headers={['Item','Expected','Actual','Difference','Investigation','Action Taken']}
        fields={['item','expected','actual','difference','investigation','action_taken']}
        template={{item:'',expected:'',actual:'',difference:'',investigation:'',action_taken:''}} onUpdateRow={updateRow} onAddRow={addRow} onRemoveRow={removeRow} s={s} />

      <InventoryTableForm title="Section I – Damaged / Rejected Materials"
        section="damaged"
        headers={['Item','Quantity','Cause','Action']}
        fields={['item','quantity','cause','action']}
        template={{item:'',quantity:'',cause:'',action:''}} />

      <InventoryTableForm title="Section J – Low Stock Alert"
        section="low_stock"
        headers={['Material','Current Balance','Minimum Level','Recommended Action']}
        fields={['material','current_balance','minimum_level','recommended_action']}
        template={{material:'',current_balance:'',minimum_level:'',recommended_action:''}} onUpdateRow={updateRow} onAddRow={addRow} onRemoveRow={removeRow} s={s} />

      <Section title="Section K – Challenges Encountered">
        <textarea className="form-control" rows={3} value={s.challenges} onChange={e=>update('challenges',e.target.value)} />
      </Section>

      <Section title="Section L – Actions Taken">
        <textarea className="form-control" rows={3} value={s.actions_taken} onChange={e=>update('actions_taken',e.target.value)} />
      </Section>

      <Section title="Section M – Tomorrow's Priorities">
        {[['Materials to prepare','materials_to_prepare'],['Job Orders to support','job_orders'],
          ['Stock counts','stock_counts'],['Procurement needs','procurement'],['Follow-ups','followups']].map(([label,key])=>(
          <div className="form-group" key={key}>
            <label className="form-label">{label}</label>
            <textarea className="form-control" rows={2} value={s.tomorrows_priorities[key]}
              onChange={e=>update('tomorrows_priorities',{...s.tomorrows_priorities,[key]:e.target.value})} />
          </div>
        ))}
      </Section>

      <Section title="Section N – Performance Metrics">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>
            {["KPI","Today's Result"].map(h=><th key={h} style={{padding:'8px 12px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}
          </tr></thead>
          <tbody>{[
            ['Materials Received','materials_received'],['Materials Prepared','materials_prepared'],
            ['Materials Issued','materials_issued'],['Returns Verified','returns_verified'],
            ['Finished Goods Received','finished_goods_received'],['Finished Goods Released','finished_goods_released'],
            ['Inventory Variances','variances'],['Low Stock Items','low_stock_items']
          ].map(([label,key])=>(
            <tr key={key}>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontSize:12}}>{label}</td>
              <td style={{padding:'4px 8px',borderBottom:'1px solid #EDE7D8'}}>
                <input className="form-control" style={{fontSize:11,padding:'4px 6px'}} value={s.kpis[key]}
                  onChange={e=>update('kpis',{...s.kpis,[key]:e.target.value})} />
              </td>
            </tr>
          ))}</tbody>
        </table>
      </Section>
    </BaseReportForm>
  );
}

function InventoryDailyDetail({ sections: s }) {
  return (
    <div>
      <Section title="Section A – Operational Summary">
        {Object.entries(s.operational_summary||{}).map(([k,v])=>v?<Field key={k} label={k.replace(/_/g,' ')} value={v} />:null)}
      </Section>
      <Section title="Section B – Raw Materials Received">
        <TableDisplay headers={['Material','Unit','Qty','Received From','Reference','Condition','Remarks']}
          rows={(s.raw_materials_received||[]).map(r=>[r.material,r.unit,r.quantity,r.received_from,r.reference,r.condition,r.remarks])} />
      </Section>
      <Section title="Section C – Materials Prepared">
        <TableDisplay headers={['Material','Qty Prepared','Job Order','Prepared By','Remarks']}
          rows={(s.materials_prepared||[]).map(r=>[r.material,r.quantity_prepared,r.job_order,r.prepared_by,r.remarks])} />
      </Section>
      <Section title="Section D – Materials Issued">
        <TableDisplay headers={['Job Order','Staff','Material','Qty','Time','Remarks']}
          rows={(s.materials_issued||[]).map(r=>[r.job_order,r.production_staff,r.material,r.quantity,r.time_issued,r.remarks])} />
      </Section>
      <Section title="Section E – Materials Returned">
        <TableDisplay headers={['Job Order','Staff','Item','Expected','Actual','Difference','Remarks']}
          rows={(s.materials_returned||[]).map(r=>[r.job_order,r.staff,r.item,r.expected_qty,r.actual_qty,r.difference,r.remarks])} />
      </Section>
      <Section title="Section F – Finished Goods Received">
        <TableDisplay headers={['Job Order','Product','Expected','Actual','QC Status','Remarks']}
          rows={(s.finished_goods_received||[]).map(r=>[r.job_order,r.product,r.expected_qty,r.actual_qty,r.qc_status,r.remarks])} />
      </Section>
      <Section title="Section G – Finished Goods Released">
        <TableDisplay headers={['Order No.','Customer','Product','Qty','Released To','Reference']}
          rows={(s.finished_goods_released||[]).map(r=>[r.order_no,r.customer,r.product,r.quantity,r.released_to,r.reference])} />
      </Section>
      <Section title="Section H – Variances">
        <TableDisplay headers={['Item','Expected','Actual','Difference','Investigation','Action']}
          rows={(s.variances||[]).map(r=>[r.item,r.expected,r.actual,r.difference,r.investigation,r.action_taken])} />
      </Section>
      <Section title="Section I – Damaged Materials">
        <TableDisplay headers={['Item','Quantity','Cause','Action']}
          rows={(s.damaged||[]).map(r=>[r.item,r.quantity,r.cause,r.action])} />
      </Section>
      <Section title="Section J – Low Stock">
        <TableDisplay headers={['Material','Current Balance','Minimum Level','Recommended Action']}
          rows={(s.low_stock||[]).map(r=>[r.material,r.current_balance,r.minimum_level,r.recommended_action])} />
      </Section>
      {s.challenges && <Section title="Section K – Challenges"><p style={{fontSize:13}}>{s.challenges}</p></Section>}
      {s.actions_taken && <Section title="Section L – Actions Taken"><p style={{fontSize:13}}>{s.actions_taken}</p></Section>}
      <Section title="Section M – Tomorrow's Priorities">
        {Object.entries(s.tomorrows_priorities||{}).map(([k,v])=>v?<Field key={k} label={k.replace(/_/g,' ')} value={v} />:null)}
      </Section>
      <Section title="Section N – Performance Metrics">
        <TableDisplay headers={['KPI','Result']}
          rows={Object.entries(s.kpis||{}).map(([k,v])=>[k.replace(/_/g,' '),v])} />
      </Section>
    </div>
  );
}

function InventoryWeeklyForm({ reportType, frequency, periodLabel, onBack, onSubmitted }) {
  const [prefilling, setPrefilling] = useState(false);
  const prefill = async () => {
    setPrefilling(true);
    try {
      const data = await get('/team-reports/prefill/inventory-weekly');
      setS(prev => ({...prev, material_preparation: data.material_preparation?.length ? data.material_preparation : prev.material_preparation, movement_summary: data.movement_summary ? {...prev.movement_summary, ...data.movement_summary} : prev.movement_summary, low_stock: data.low_stock?.length ? data.low_stock : prev.low_stock, finished_goods: data.finished_goods?.length ? data.finished_goods : prev.finished_goods, production_support: data.production_support?.length ? data.production_support : prev.production_support}));
      alert('Data pre-filled! Review and edit before submitting.');
    } catch (err) { alert('Could not prefill: '+err.message); }
    finally { setPrefilling(false); }
  };
  const [s, setS] = useState({
    executive_summary: '',
    movement_summary: { raw_materials_received:'', materials_prepared:'', material_issues:'', material_returns:'', finished_goods_received:'', finished_goods_released:'', adjustments:'' },
    production_support: [{ job_order:'', customer:'', status:'', materials_prepared:'', production_supported:'', remarks:'' }],
    material_preparation: [{ material:'', quantity_prepared:'', job_orders_supported:'', remarks:'' }],
    accuracy: { counts_conducted:'', variances_identified:'', variances_resolved:'', outstanding_variances:'' },
    wip_summary: [{ stage:'', opening:'', received:'', issued:'', closing:'' }],
    finished_goods: [{ product:'', qty_received:'', qty_released:'', closing_balance:'' }],
    low_stock: [{ material:'', current_balance:'', reorder_level:'', recommended_purchase:'', priority:'High' }],
    damaged: [{ material:'', quantity:'', cause:'', corrective:'', preventive:'' }],
    store_assessment: [
      { area:'Store Organization', status:'Good', comments:'' },
      { area:'Labelling', status:'Good', comments:'' },
      { area:'Cleanliness', status:'Good', comments:'' },
      { area:'Security', status:'Good', comments:'' },
      { area:'Documentation', status:'Good', comments:'' },
    ],
    challenges: '',
    corrective_actions: '',
    recommendations: '',
    kpis: { inventory_accuracy:'', material_return_accuracy:'', job_orders_on_time:'', variances_resolved:'', low_stock_alerts:'', documentation:'', finished_goods_reconciliation:'', wip_reconciliation:'' },
    next_week_priorities: { job_orders:'', materials_to_prepare:'', stock_counts:'', procurement:'', store_improvements:'', risks:'' }
  });
  const update = (section, val) => setS(prev => ({...prev, [section]: val}));
  const updateRow = (section, idx, field, val) => { const rows = [...s[section]]; rows[idx] = {...rows[idx], [field]: val}; update(section, rows); };
  const addRow = (section, template) => update(section, [...s[section], {...template}]);
  const removeRow = (section, idx) => update(section, s[section].filter((_,i)=>i!==idx));

  return (
    <BaseReportForm title="Weekly Inventory Report" reportType={reportType} frequency={frequency}
      periodLabel={periodLabel} onBack={onBack} onSubmitted={onSubmitted} sections={s} setSections={setS}>
      <div style={{background:'#E8F5EB',border:'1px solid #B8DEC0',borderRadius:8,padding:'12px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:13,color:'#2E6B3A',fontWeight:500}}>📊 Auto-fill from BloansBooks data</div>
        <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none'}} onClick={prefill} disabled={prefilling}>
          {prefilling ? 'Loading...' : '⚡ Pre-fill from BloansBooks'}
        </button>
      </div>

      <Section title="Section A – Weekly Executive Summary">
        <textarea className="form-control" rows={6} placeholder="Provide a concise summary (150-300 words) of the week's inventory operations..."
          value={s.executive_summary} onChange={e=>update('executive_summary',e.target.value)} />
      </Section>

      <Section title="Section B – Weekly Inventory Movement Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>
            {['Activity','Total for the Week'].map(h=><th key={h} style={{padding:'8px 12px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}
          </tr></thead>
          <tbody>{[
            ['Raw Materials Received','raw_materials_received'],['Materials Prepared','materials_prepared'],
            ['Material Issue Transactions','material_issues'],['Material Return Transactions','material_returns'],
            ['Finished Goods Received','finished_goods_received'],['Finished Goods Released','finished_goods_released'],
            ['Inventory Adjustments','adjustments']
          ].map(([label,key])=>(
            <tr key={key}>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontSize:12}}>{label}</td>
              <td style={{padding:'4px 8px',borderBottom:'1px solid #EDE7D8'}}>
                <input className="form-control" style={{fontSize:11,padding:'4px 6px'}} value={s.movement_summary[key]}
                  onChange={e=>update('movement_summary',{...s.movement_summary,[key]:e.target.value})} />
              </td>
            </tr>
          ))}</tbody>
        </table>
      </Section>

      <Section title="Section C – Production Support Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Job Order','Customer','Status','Materials Prepared','Production Supported','Remarks',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.production_support.map((row,i)=>(
            <tr key={i}>{['job_order','customer','status','materials_prepared','production_supported','remarks'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('production_support',i,f,e.target.value)} /></td>
            ))}<td><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('production_support',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('production_support',{job_order:'',customer:'',status:'',materials_prepared:'',production_supported:'',remarks:''})}>+ Add Row</button>
      </Section>

      <Section title="Section D – Material Preparation Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Material','Qty Prepared','Job Orders Supported','Remarks',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.material_preparation.map((row,i)=>(
            <tr key={i}>{['material','quantity_prepared','job_orders_supported','remarks'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('material_preparation',i,f,e.target.value)} /></td>
            ))}<td><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('material_preparation',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('material_preparation',{material:'',quantity_prepared:'',job_orders_supported:'',remarks:''})}>+ Add Row</button>
      </Section>

      <Section title="Section E – Inventory Accuracy & Reconciliation">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {[['Inventory Counts Conducted','counts_conducted'],['Variances Identified','variances_identified'],
            ['Variances Resolved','variances_resolved'],['Outstanding Variances','outstanding_variances']].map(([label,key])=>(
            <div className="form-group" key={key}>
              <label className="form-label">{label}</label>
              <input className="form-control" value={s.accuracy[key]} onChange={e=>update('accuracy',{...s.accuracy,[key]:e.target.value})} />
            </div>
          ))}
        </div>
      </Section>

      <Section title="Section F – Work-in-Progress (WIP) Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Production Stage','Opening Balance','Received','Issued','Closing Balance',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.wip_summary.map((row,i)=>(
            <tr key={i}>{['stage','opening','received','issued','closing'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('wip_summary',i,f,e.target.value)} /></td>
            ))}<td><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('wip_summary',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('wip_summary',{stage:'',opening:'',received:'',issued:'',closing:''})}>+ Add Row</button>
      </Section>

      <Section title="Section G – Finished Goods Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Product','Qty Received','Qty Released','Closing Balance',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.finished_goods.map((row,i)=>(
            <tr key={i}>{['product','qty_received','qty_released','closing_balance'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('finished_goods',i,f,e.target.value)} /></td>
            ))}<td><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('finished_goods',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('finished_goods',{product:'',qty_received:'',qty_released:'',closing_balance:''})}>+ Add Row</button>
      </Section>

      <Section title="Section H – Low Stock & Procurement Recommendations">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Material','Current Balance','Reorder Level','Recommended Purchase','Priority',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.low_stock.map((row,i)=>(
            <tr key={i}>{['material','current_balance','reorder_level','recommended_purchase'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('low_stock',i,f,e.target.value)} /></td>
            ))}
            <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.priority} onBlur={e=>updateRow('low_stock',i,'priority',e.target.value)}>
              <option>Critical</option><option>High</option><option>Medium</option><option>Low</option>
            </select></td>
            <td><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('low_stock',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('low_stock',{material:'',current_balance:'',reorder_level:'',recommended_purchase:'',priority:'High'})}>+ Add Row</button>
      </Section>

      <Section title="Section I – Damaged, Lost & Wastage">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Material','Quantity','Cause','Corrective Action','Preventive Action',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.damaged.map((row,i)=>(
            <tr key={i}>{['material','quantity','cause','corrective','preventive'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('damaged',i,f,e.target.value)} /></td>
            ))}<td><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('damaged',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('damaged',{material:'',quantity:'',cause:'',corrective:'',preventive:''})}>+ Add Row</button>
      </Section>

      <Section title="Section J – Store Management Assessment">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Area','Status','Comments'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.store_assessment.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'8px 10px',fontWeight:500,fontSize:12}}>{row.area}</td>
              <td style={{padding:'4px'}}><select className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.status} onBlur={e=>updateRow('store_assessment',i,'status',e.target.value)}>
                <option>Excellent</option><option>Good</option><option>Fair</option><option>Poor</option>
              </select></td>
              <td style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row.comments} onBlur={e=>updateRow('store_assessment',i,'comments',e.target.value)} /></td>
            </tr>
          ))}</tbody>
        </table>
      </Section>

      <Section title="Section K – Key Challenges">
        <textarea className="form-control" rows={3} value={s.challenges} onChange={e=>update('challenges',e.target.value)} />
      </Section>
      <Section title="Section L – Corrective Actions Taken">
        <textarea className="form-control" rows={3} value={s.corrective_actions} onChange={e=>update('corrective_actions',e.target.value)} />
      </Section>
      <Section title="Section M – Recommendations">
        <textarea className="form-control" rows={3} value={s.recommendations} onChange={e=>update('recommendations',e.target.value)} />
      </Section>

      <Section title="Section N – Weekly KPI Dashboard">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['KPI','Target','Actual','Status'].map(h=><th key={h} style={{padding:'8px 12px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{[
            ['Inventory Accuracy','≥99%','inventory_accuracy'],
            ['Material Return Accuracy','100%','material_return_accuracy'],
            ['Job Orders Supported On Time','100%','job_orders_on_time'],
            ['Inventory Variances Resolved','100%','variances_resolved'],
            ['Low Stock Alerts Raised','100%','low_stock_alerts'],
            ['Documentation Compliance','100%','documentation'],
            ['Finished Goods Reconciliation','100%','finished_goods_reconciliation'],
            ['WIP Reconciliation','100%','wip_reconciliation'],
          ].map(([label,target,key])=>(
            <tr key={key}>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontSize:12}}>{label}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#556B2F',fontWeight:600}}>{target}</td>
              <td style={{padding:'4px 8px',borderBottom:'1px solid #EDE7D8'}}>
                <input className="form-control" style={{fontSize:11,padding:'4px 6px'}} value={s.kpis[key]}
                  onChange={e=>update('kpis',{...s.kpis,[key]:e.target.value})} />
              </td>
              <td style={{padding:'4px 8px',borderBottom:'1px solid #EDE7D8'}}>
                <select className="form-control" style={{fontSize:11,padding:'4px 6px'}}>
                  <option>Met</option><option>Not Met</option><option>Partial</option>
                </select>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </Section>

      <Section title="Section O – Priorities for Next Week">
        {[['Job Orders requiring support','job_orders'],['Materials to prepare','materials_to_prepare'],
          ['Stock counts planned','stock_counts'],['Procurement follow-up','procurement'],
          ['Store improvements','store_improvements'],['Anticipated risks','risks']].map(([label,key])=>(
          <div className="form-group" key={key}>
            <label className="form-label">{label}</label>
            <textarea className="form-control" rows={2} value={s.next_week_priorities[key]}
              onChange={e=>update('next_week_priorities',{...s.next_week_priorities,[key]:e.target.value})} />
          </div>
        ))}
      </Section>
    </BaseReportForm>
  );
}

function InventoryWeeklyDetail({ sections: s }) {
  return (
    <div>
      {s.executive_summary && <Section title="A – Executive Summary"><p style={{fontSize:13,lineHeight:1.6}}>{s.executive_summary}</p></Section>}
      <Section title="B – Movement Summary">
        <TableDisplay headers={['Activity','Total']} rows={Object.entries(s.movement_summary||{}).map(([k,v])=>[k.replace(/_/g,' '),v])} />
      </Section>
      <Section title="C – Production Support">
        <TableDisplay headers={['Job Order','Customer','Status','Materials Prepared','Supported','Remarks']}
          rows={(s.production_support||[]).map(r=>[r.job_order,r.customer,r.status,r.materials_prepared,r.production_supported,r.remarks])} />
      </Section>
      <Section title="D – Material Preparation">
        <TableDisplay headers={['Material','Qty Prepared','Job Orders Supported','Remarks']}
          rows={(s.material_preparation||[]).map(r=>[r.material,r.quantity_prepared,r.job_orders_supported,r.remarks])} />
      </Section>
      <Section title="E – Inventory Accuracy">
        {Object.entries(s.accuracy||{}).map(([k,v])=><Field key={k} label={k.replace(/_/g,' ')} value={v} />)}
      </Section>
      <Section title="F – WIP Summary">
        <TableDisplay headers={['Stage','Opening','Received','Issued','Closing']}
          rows={(s.wip_summary||[]).map(r=>[r.stage,r.opening,r.received,r.issued,r.closing])} />
      </Section>
      <Section title="G – Finished Goods">
        <TableDisplay headers={['Product','Received','Released','Closing Balance']}
          rows={(s.finished_goods||[]).map(r=>[r.product,r.qty_received,r.qty_released,r.closing_balance])} />
      </Section>
      <Section title="H – Low Stock">
        <TableDisplay headers={['Material','Balance','Reorder Level','Recommended Purchase','Priority']}
          rows={(s.low_stock||[]).map(r=>[r.material,r.current_balance,r.reorder_level,r.recommended_purchase,r.priority])} />
      </Section>
      <Section title="I – Damaged/Wastage">
        <TableDisplay headers={['Material','Qty','Cause','Corrective','Preventive']}
          rows={(s.damaged||[]).map(r=>[r.material,r.quantity,r.cause,r.corrective,r.preventive])} />
      </Section>
      <Section title="J – Store Assessment">
        <TableDisplay headers={['Area','Status','Comments']}
          rows={(s.store_assessment||[]).map(r=>[r.area,r.status,r.comments])} />
      </Section>
      {s.challenges && <Section title="K – Challenges"><p style={{fontSize:13}}>{s.challenges}</p></Section>}
      {s.corrective_actions && <Section title="L – Corrective Actions"><p style={{fontSize:13}}>{s.corrective_actions}</p></Section>}
      {s.recommendations && <Section title="M – Recommendations"><p style={{fontSize:13}}>{s.recommendations}</p></Section>}
      <Section title="N – KPI Dashboard">
        <TableDisplay headers={['KPI','Actual']} rows={Object.entries(s.kpis||{}).map(([k,v])=>[k.replace(/_/g,' '),v])} />
      </Section>
    </div>
  );
}

function InventoryMonthlyForm({ reportType, frequency, periodLabel, onBack, onSubmitted }) {
  const [prefilling, setPrefilling] = useState(false);
  const prefill = async () => {
    setPrefilling(true);
    try {
      const data = await get('/team-reports/prefill/inventory-monthly');
      setS(prev => ({...prev, activity_summary: data.activity_summary ? {...prev.activity_summary, ...data.activity_summary} : prev.activity_summary, production_support: data.production_support ? {...prev.production_support, ...data.production_support} : prev.production_support, reconciliation: data.reconciliation?.length ? data.reconciliation : prev.reconciliation, procurement: data.procurement?.length ? data.procurement : prev.procurement}));
      alert('Data pre-filled! Review and edit before submitting.');
    } catch (err) { alert('Could not prefill: '+err.message); }
    finally { setPrefilling(false); }
  };
  const [s, setS] = useState({
    executive_summary: '',
    activity_summary: { materials_received:'', materials_prepared:'', materials_issued:'', semi_finished_returned:'', finished_goods_received:'', finished_goods_released:'' },
    production_support: { job_orders_supported:'', completed:'', delayed:'', emergency:'', avg_prep_time:'' },
    variance_analysis: { total_variances:'', resolved:'', outstanding:'', investigations_completed:'' },
    reconciliation: [
      { category:'Raw Materials', system_qty:'', physical_qty:'', difference:'' },
      { category:'Prepared Materials', system_qty:'', physical_qty:'', difference:'' },
      { category:'WIP Inventory', system_qty:'', physical_qty:'', difference:'' },
      { category:'Finished Goods', system_qty:'', physical_qty:'', difference:'' },
    ],
    wastage: [{ material:'', quantity_lost:'', cause:'', preventive:'' }],
    stock_movement: { fast_moving:'', slow_moving:'', dead_stock:'', frequently_requested:'', overstocked:'' },
    procurement: [{ material:'', current_stock:'', suggested_purchase:'', reason:'' }],
    store_assessment: '',
    process_improvements: '',
    kpis: { inventory_accuracy:'', material_return_accuracy:'', finished_goods_reconciliation:'', stock_count_completion:'', variance_rate:'', low_stock_response:'', production_support_timeliness:'', documentation_compliance:'' },
    risks: '',
    action_plan: ''
  });
  const update = (section, val) => setS(prev => ({...prev, [section]: val}));
  const updateRow = (section, idx, field, val) => { const rows = [...s[section]]; rows[idx] = {...rows[idx], [field]: val}; update(section, rows); };
  const addRow = (section, template) => update(section, [...s[section], {...template}]);
  const removeRow = (section, idx) => update(section, s[section].filter((_,i)=>i!==idx));

  return (
    <BaseReportForm title="Monthly Inventory Report" reportType={reportType} frequency={frequency}
      periodLabel={periodLabel} onBack={onBack} onSubmitted={onSubmitted} sections={s} setSections={setS}>
      <div style={{background:'#E8F5EB',border:'1px solid #B8DEC0',borderRadius:8,padding:'12px 16px',marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:13,color:'#2E6B3A',fontWeight:500}}>📊 Auto-fill from BloansBooks data</div>
        <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none'}} onClick={prefill} disabled={prefilling}>
          {prefilling ? 'Loading...' : '⚡ Pre-fill from BloansBooks'}
        </button>
      </div>

      <Section title="Section A – Monthly Executive Summary">
        <textarea className="form-control" rows={6} placeholder="Overall inventory performance, major achievements, key challenges, risks, recommendations..."
          value={s.executive_summary} onChange={e=>update('executive_summary',e.target.value)} />
      </Section>

      <Section title="Section B – Inventory Activity Summary">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Activity','Monthly Total'].map(h=><th key={h} style={{padding:'8px 12px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{[
            ['Materials Received','materials_received'],['Materials Prepared','materials_prepared'],
            ['Materials Issued','materials_issued'],['Semi-finished Items Returned','semi_finished_returned'],
            ['Finished Goods Received','finished_goods_received'],['Finished Goods Released','finished_goods_released']
          ].map(([label,key])=>(
            <tr key={key}>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontSize:12}}>{label}</td>
              <td style={{padding:'4px 8px',borderBottom:'1px solid #EDE7D8'}}>
                <input className="form-control" style={{fontSize:11,padding:'4px 6px'}} value={s.activity_summary[key]}
                  onChange={e=>update('activity_summary',{...s.activity_summary,[key]:e.target.value})} />
              </td>
            </tr>
          ))}</tbody>
        </table>
      </Section>

      <Section title="Section C – Production Support Analysis">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {[['Job Orders Supported','job_orders_supported'],['Completed','completed'],
            ['Delayed Due to Inventory','delayed'],['Emergency Requests','emergency'],
            ['Average Material Preparation Time','avg_prep_time']].map(([label,key])=>(
            <div className="form-group" key={key}>
              <label className="form-label">{label}</label>
              <input className="form-control" value={s.production_support[key]}
                onChange={e=>update('production_support',{...s.production_support,[key]:e.target.value})} />
            </div>
          ))}
        </div>
      </Section>

      <Section title="Section D – Inventory Variance Analysis">
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {[['Total Variances','total_variances'],['Resolved','resolved'],
            ['Outstanding','outstanding'],['Investigations Completed','investigations_completed']].map(([label,key])=>(
            <div className="form-group" key={key}>
              <label className="form-label">{label}</label>
              <input className="form-control" value={s.variance_analysis[key]}
                onChange={e=>update('variance_analysis',{...s.variance_analysis,[key]:e.target.value})} />
            </div>
          ))}
        </div>
      </Section>

      <Section title="Section E – Inventory Reconciliation">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Category','System Quantity','Physical Quantity','Difference'].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.reconciliation.map((row,i)=>(
            <tr key={i}>
              <td style={{padding:'8px 10px',fontWeight:500,fontSize:12}}>{row.category}</td>
              {['system_qty','physical_qty','difference'].map(f=>(
                <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('reconciliation',i,f,e.target.value)} /></td>
              ))}
            </tr>
          ))}</tbody>
        </table>
      </Section>

      <Section title="Section F – Wastage Analysis">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Material','Quantity Lost','Cause','Preventive Action',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.wastage.map((row,i)=>(
            <tr key={i}>{['material','quantity_lost','cause','preventive'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('wastage',i,f,e.target.value)} /></td>
            ))}<td><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('wastage',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('wastage',{material:'',quantity_lost:'',cause:'',preventive:''})}>+ Add Row</button>
      </Section>

      <Section title="Section G – Stock Movement Analysis">
        {[['Fast-moving materials','fast_moving'],['Slow-moving materials','slow_moving'],
          ['Dead stock','dead_stock'],['Frequently requested materials','frequently_requested'],
          ['Overstocked items','overstocked']].map(([label,key])=>(
          <div className="form-group" key={key}>
            <label className="form-label">{label}</label>
            <textarea className="form-control" rows={2} value={s.stock_movement[key]}
              onChange={e=>update('stock_movement',{...s.stock_movement,[key]:e.target.value})} />
          </div>
        ))}
      </Section>

      <Section title="Section H – Procurement Recommendations">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12,marginBottom:8}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['Material','Current Stock','Suggested Purchase','Reason',''].map(h=><th key={h} style={{padding:'6px 8px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{s.procurement.map((row,i)=>(
            <tr key={i}>{['material','current_stock','suggested_purchase','reason'].map(f=>(
              <td key={f} style={{padding:'4px'}}><input className="form-control" style={{fontSize:11,padding:'4px 6px'}} defaultValue={row[f]} onBlur={e=>updateRow('procurement',i,f,e.target.value)} /></td>
            ))}<td><button className="btn btn-ghost btn-sm" onClick={()=>removeRow('procurement',i)} style={{color:'var(--unpaid)',padding:'2px 6px'}}>×</button></td></tr>
          ))}</tbody>
        </table>
        <button className="btn btn-outline btn-sm" onClick={()=>addRow('procurement',{material:'',current_stock:'',suggested_purchase:'',reason:''})}>+ Add Row</button>
      </Section>

      <Section title="Section I – Store Management Assessment">
        <textarea className="form-control" rows={4} placeholder="Assess store organization, labeling, housekeeping, inventory security, compliance..."
          value={s.store_assessment} onChange={e=>update('store_assessment',e.target.value)} />
      </Section>

      <Section title="Section J – Process Improvement Opportunities">
        <textarea className="form-control" rows={4} placeholder="Recommend improvements for material flow, inventory controls, production support, documentation..."
          value={s.process_improvements} onChange={e=>update('process_improvements',e.target.value)} />
      </Section>

      <Section title="Section K – Monthly KPI Dashboard">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'#F2EFE7'}}>{['KPI','Target','Actual','Performance'].map(h=><th key={h} style={{padding:'8px 12px',fontSize:9,color:'#556B2F',fontWeight:600,textTransform:'uppercase',textAlign:'left'}}>{h}</th>)}</tr></thead>
          <tbody>{[
            ['Inventory Accuracy','99%','inventory_accuracy'],
            ['Material Return Accuracy','100%','material_return_accuracy'],
            ['Finished Goods Reconciliation','100%','finished_goods_reconciliation'],
            ['Stock Count Completion','100%','stock_count_completion'],
            ['Inventory Variance Rate','≤1%','variance_rate'],
            ['Low Stock Response Time','100%','low_stock_response'],
            ['Production Support Timeliness','100%','production_support_timeliness'],
            ['Documentation Compliance','100%','documentation_compliance'],
          ].map(([label,target,key])=>(
            <tr key={key}>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontSize:12}}>{label}</td>
              <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#556B2F',fontWeight:600}}>{target}</td>
              <td style={{padding:'4px 8px',borderBottom:'1px solid #EDE7D8'}}>
                <input className="form-control" style={{fontSize:11,padding:'4px 6px'}} value={s.kpis[key]}
                  onChange={e=>update('kpis',{...s.kpis,[key]:e.target.value})} />
              </td>
              <td style={{padding:'4px 8px',borderBottom:'1px solid #EDE7D8'}}>
                <select className="form-control" style={{fontSize:11,padding:'4px 6px'}}>
                  <option>Met</option><option>Not Met</option><option>Partial</option>
                </select>
              </td>
            </tr>
          ))}</tbody>
        </table>
      </Section>

      <Section title="Section L – Key Risks for Next Month">
        <textarea className="form-control" rows={4} placeholder="Materials likely to run low, seasonal demand changes, supplier concerns, capacity constraints..."
          value={s.risks} onChange={e=>update('risks',e.target.value)} />
      </Section>

      <Section title="Section M – Action Plan for Next Month">
        <textarea className="form-control" rows={4} placeholder="List planned actions, responsible persons, and expected outcomes..."
          value={s.action_plan} onChange={e=>update('action_plan',e.target.value)} />
      </Section>
    </BaseReportForm>
  );
}

function InventoryMonthlyDetail({ sections: s }) {
  return (
    <div>
      {s.executive_summary && <Section title="A – Executive Summary"><p style={{fontSize:13,lineHeight:1.6}}>{s.executive_summary}</p></Section>}
      <Section title="B – Activity Summary">
        <TableDisplay headers={['Activity','Monthly Total']} rows={Object.entries(s.activity_summary||{}).map(([k,v])=>[k.replace(/_/g,' '),v])} />
      </Section>
      <Section title="C – Production Support">
        {Object.entries(s.production_support||{}).map(([k,v])=><Field key={k} label={k.replace(/_/g,' ')} value={v} />)}
      </Section>
      <Section title="D – Variance Analysis">
        {Object.entries(s.variance_analysis||{}).map(([k,v])=><Field key={k} label={k.replace(/_/g,' ')} value={v} />)}
      </Section>
      <Section title="E – Reconciliation">
        <TableDisplay headers={['Category','System Qty','Physical Qty','Difference']}
          rows={(s.reconciliation||[]).map(r=>[r.category,r.system_qty,r.physical_qty,r.difference])} />
      </Section>
      <Section title="F – Wastage">
        <TableDisplay headers={['Material','Qty Lost','Cause','Preventive Action']}
          rows={(s.wastage||[]).map(r=>[r.material,r.quantity_lost,r.cause,r.preventive])} />
      </Section>
      <Section title="H – Procurement Recommendations">
        <TableDisplay headers={['Material','Current Stock','Suggested Purchase','Reason']}
          rows={(s.procurement||[]).map(r=>[r.material,r.current_stock,r.suggested_purchase,r.reason])} />
      </Section>
      <Section title="K – KPI Dashboard">
        <TableDisplay headers={['KPI','Actual']} rows={Object.entries(s.kpis||{}).map(([k,v])=>[k.replace(/_/g,' '),v])} />
      </Section>
      {s.risks && <Section title="L – Key Risks"><p style={{fontSize:13}}>{s.risks}</p></Section>}
      {s.action_plan && <Section title="M – Action Plan"><p style={{fontSize:13}}>{s.action_plan}</p></Section>}
    </div>
  );
}
